import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { getCommunityId } from '../../../utils/community';
import { getClubId } from '../../../utils/club';
import { fetchMatchById, startMatch, updateMatchConfiguration, updateMatchScore } from '../../../services/matchService';
import PageShell from '../../../components/layout/PageShell';
import { getMatchStartDestination, hasFirstBallBowled, isTossCompleted } from '../../../utils/matchStatus';
import { buildPlayerDirectory, normalizeId, uniqueById } from '../utils/playerDirectory';
import { buildBatterDropdownOptions, buildDismissedPlayerIds } from '../utils/batterDropdownOptions';
import { buildBowlerDropdownOptions } from '../utils/bowlerDropdownOptions';
import {
  buildMatchManagerMatchStartPath,
  buildMatchManagerMatchTossPath,
  buildMatchManagerTournamentPath,
} from '../../../utils/matchManagerPaths';
import { classifyScoringMutationError, recoveryDelayMs, SCORING_MUTATION_STATUS } from '../utils/scoringMutationRecovery';
import { isPendingMutationExpired, listPendingScoringMutations, removePendingScoringMutation, savePendingScoringMutation } from '../utils/pendingScoringMutationStorage';

const createScoringRequestId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `score-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};
const pendingScoringMutationStore = new Map();

const oversFromBalls = (balls = 0) => {
  const safeBalls = Number(balls) || 0;
  if (safeBalls <= 0) {
    return '0.0';
  }
  const overs = Math.floor((safeBalls - 1) / 6);
  const remaining = ((safeBalls - 1) % 6) + 1;
  return `${overs}.${remaining}`;
};

const normalizePowerPlays = (powerPlays = []) => {
  if (!Array.isArray(powerPlays)) return [];
  return powerPlays
    .map((entry) => ({
      startOver: Number(entry?.startOver),
      endOver: Number(entry?.endOver ?? entry?.startOver),
    }))
    .filter(
      (entry) =>
        Number.isFinite(entry.startOver) &&
        entry.startOver > 0 &&
        Number.isFinite(entry.endOver) &&
        entry.endOver > 0
    );
};

const formatPowerPlayLabel = (entry) =>
  entry.startOver === entry.endOver
    ? `Over ${entry.startOver}`
    : `Overs ${entry.startOver}-${entry.endOver}`;

const formatPowerPlaySummary = (powerPlays) =>
  (powerPlays || []).map(formatPowerPlayLabel).join(', ');

const normalizeTossDecision = (value) => (value || '').trim().toLowerCase();

const computeInningsTeams = ({ team1 = '', team2 = '', tossWinner = '', tossDecision = '' } = {}) => {
  const normalizedTeam1 = (team1 || '').trim();
  const normalizedTeam2 = (team2 || '').trim();
  const normalizedWinner = (tossWinner || '').trim();
  const normalizedDecision = normalizeTossDecision(tossDecision);

  if (!normalizedTeam1 || !normalizedTeam2) {
    return { battingTeam: normalizedTeam1 || '', bowlingTeam: normalizedTeam2 || '' };
  }

  if (normalizedWinner !== normalizedTeam1 && normalizedWinner !== normalizedTeam2) {
    return { battingTeam: normalizedTeam1, bowlingTeam: normalizedTeam2 };
  }

  if (normalizedDecision !== 'bat' && normalizedDecision !== 'field') {
    return { battingTeam: normalizedTeam1, bowlingTeam: normalizedTeam2 };
  }

  const otherTeam = normalizedWinner === normalizedTeam1 ? normalizedTeam2 : normalizedTeam1;
  return normalizedDecision === 'bat'
    ? { battingTeam: normalizedWinner, bowlingTeam: otherTeam }
    : { battingTeam: otherTeam, bowlingTeam: normalizedWinner };
};

const extraTypeLabels = {
  wide: 'Wd',
  'no-ball': 'Nb',
  bye: 'B',
  'leg-bye': 'Lb',
  penalty: 'P',
  'jackpot-plus': 'JP',
  'jackpot-minus': 'JM',
};

const wicketTypeOptions = [
  { value: 'bowled', label: 'Bowled' },
  { value: 'caught', label: 'Caught' },
  { value: 'caught-and-bowled', label: 'Caught and Bowled' },
  { value: 'lbw', label: 'LBW' },
  { value: 'run-out', label: 'Run out' },
  { value: 'stumped', label: 'Stumped' },
  { value: 'hit-wicket', label: 'Hit wicket' },
  { value: 'retired-hurt', label: 'Retired hurt' },
  { value: 'retired-out', label: 'Retired out' },
  { value: 'obstructing-the-field', label: 'Obstructing the field' },
  { value: 'hit-the-ball-twice', label: 'Hit the ball twice' },
  { value: 'handled-ball', label: 'Handled the ball' },
  { value: 'timed-out', label: 'Timed out' },
];
const wicketTypeLabels = new Map(wicketTypeOptions.map((option) => [option.value, option.label]));
const formatWicketTypeLabel = (wicketType = '') =>
  wicketTypeLabels.get(wicketType) || (wicketType ? wicketType.replace(/-/g, ' ') : '');
const getRetirementTypeFromEvent = (event) => {
  const wicketType = typeof event?.wicketType === 'string' ? event.wicketType : '';
  return wicketType.startsWith('retired') ? wicketType : '';
};
const isRetiredHurt = (event) => Boolean(event?.isWicket && getRetirementTypeFromEvent(event) === 'retired-hurt');
const isActualWicket = (event) => Boolean(event?.isWicket && !getRetirementTypeFromEvent(event));

const wicketTypesWithoutFieldingDetails = new Set([
  'bowled',
  'caught-and-bowled',
  'lbw',
  'retired-out',
  'hit-the-ball-twice',
  'handled-ball',
  'timed-out',
  'obstructing-the-field',
  'hit-wicket',
]);
const shouldShowWicketFieldingDetails = (event) =>
  !isRetiredHurt(event) && !wicketTypesWithoutFieldingDetails.has(event?.wicketType);
const shouldCaptureWicketLocation = (wicketType) =>
  Boolean(wicketType) &&
  !isRunOutType(wicketType) &&
  wicketType !== 'retired-hurt' &&
  !wicketTypesWithoutFieldingDetails.has(wicketType);

const wicketLocationOptions = [
  { value: 'slip', label: 'Slip' },
  { value: 'gully', label: 'Gully' },
  { value: 'point', label: 'Point' },
  { value: 'cover', label: 'Cover' },
  { value: 'mid-off', label: 'Mid-off' },
  { value: 'mid-on', label: 'Mid-on' },
  { value: 'midwicket', label: 'Midwicket' },
  { value: 'square-leg', label: 'Square leg' },
  { value: 'fine-leg', label: 'Fine leg' },
  { value: 'third-man', label: 'Third man' },
  { value: 'long-off', label: 'Long off' },
  { value: 'long-on', label: 'Long on' },
  { value: 'deep-cover', label: 'Deep cover' },
  { value: 'deep-midwicket', label: 'Deep midwicket' },
  { value: 'deep-square-leg', label: 'Deep square leg' },
  { value: 'long-leg', label: 'Long leg' },
  { value: 'deep-extra-cover', label: 'Deep extra cover' },
  { value: 'backward-point', label: 'Backward point' },
  { value: 'backward-square-leg', label: 'Backward square leg' },
  { value: 'short-leg', label: 'Short leg' },
  { value: 'silly-point', label: 'Silly point' },
  { value: 'wicketkeeper', label: 'Wicketkeeper' },
];

const shotTypeOptions = [
  { value: 'Drive', label: 'Drive' },
  { value: 'Cover drive', label: 'Cover drive' },
  { value: 'Straight drive', label: 'Straight drive' },
  { value: 'On drive', label: 'On drive' },
  { value: 'Off drive', label: 'Off drive' },
  { value: 'Pull', label: 'Pull' },
  { value: 'Hook', label: 'Hook' },
  { value: 'Cut', label: 'Cut' },
  { value: 'Square cut', label: 'Square cut' },
  { value: 'Late cut', label: 'Late cut' },
  { value: 'Sweep', label: 'Sweep' },
  { value: 'Slog sweep', label: 'Slog sweep' },
  { value: 'Reverse sweep', label: 'Reverse sweep' },
  { value: 'Glance', label: 'Glance' },
  { value: 'Flick', label: 'Flick' },
  { value: 'Paddle', label: 'Paddle' },
  { value: 'Scoop', label: 'Scoop' },
  { value: 'Lofted drive', label: 'Lofted drive' },
  { value: 'Backfoot punch', label: 'Backfoot punch' },
  { value: 'Defensive block', label: 'Defensive block' },
];

const shotPlacementOptions = [
  { value: 'Slip', label: 'Slip' },
  { value: 'Gully', label: 'Gully' },
  { value: 'Point', label: 'Point' },
  { value: 'Backward point', label: 'Backward point' },
  { value: 'Cover', label: 'Cover' },
  { value: 'Extra cover', label: 'Extra cover' },
  { value: 'Mid-off', label: 'Mid-off' },
  { value: 'Mid-on', label: 'Mid-on' },
  { value: 'Midwicket', label: 'Midwicket' },
  { value: 'Square leg', label: 'Square leg' },
  { value: 'Backward square leg', label: 'Backward square leg' },
  { value: 'Fine leg', label: 'Fine leg' },
  { value: 'Third man', label: 'Third man' },
  { value: 'Long off', label: 'Long off' },
  { value: 'Long on', label: 'Long on' },
  { value: 'Deep cover', label: 'Deep cover' },
  { value: 'Deep extra cover', label: 'Deep extra cover' },
  { value: 'Deep midwicket', label: 'Deep midwicket' },
  { value: 'Deep square leg', label: 'Deep square leg' },
  { value: 'Long leg', label: 'Long leg' },
  { value: 'Straight', label: 'Straight' },
];

const wicketTypesRequiringFielder = new Set(['caught', 'run-out', 'stumped']);
const isRetiredOutWicketType = (wicketType = '') =>
  typeof wicketType === 'string' && wicketType.trim().toLowerCase() === 'retired-out';
const isRunOutType = (wicketType = '') =>
  String(wicketType).trim().toLowerCase().replace(/_/g, '-') === 'run-out';

const extractRunsFromResult = (result) => {
  const match = String(result ?? '').match(/-?\d+/);
  if (!match) return null;
  const value = Number(match[0]);
  return Number.isFinite(value) ? value : null;
};

const resolveExtraDisplayRuns = (event) => {
  const rawRuns = Number(event?.runs);
  const resultRuns = extractRunsFromResult(event?.result);
  const totalRuns =
    Number.isFinite(rawRuns) && (rawRuns !== 0 || !Number.isFinite(resultRuns) || resultRuns === 0)
      ? rawRuns
      : resultRuns ?? 0;
  const extraType = event?.extraType || '';
  if (extraType === 'jackpot-minus') {
    return Math.abs(totalRuns);
  }
  if (extraType === 'no-ball') {
    const recordedBatRuns = Number(event?.batRuns);
    if (Number.isFinite(recordedBatRuns)) {
      return Math.max(0, recordedBatRuns);
    }
    return Math.max(0, totalRuns - 1);
  }
  if (extraType === 'wide') {
    return Math.max(0, totalRuns - 1);
  }
  return Math.max(0, totalRuns);
};

const formatBallDisplay = (event) => {
  if (!event) return '';
  if (!event.countBall) return null;
  if (event.wicketType === 'retired-out' || event.wicketType === 'retired-hurt') return null;
  if (isActualWicket(event)) return 'W';
  if (isRetiredHurt(event)) return 'RH';
  if (event.extraType) {
    const runs = resolveExtraDisplayRuns(event);
    const extraLabel = extraTypeLabels[event.extraType] || event.extraType;
    return runs > 0 ? `${runs}${extraLabel}` : extraLabel;
  }
  return `${event.runs ?? 0}`;
};

const formatBallDetails = (event, resolvePlayerName) => {
  if (!event) return '';
  const details = [];
  if (event.shotType) details.push(`Shot: ${event.shotType}`);
  if (event.shotPlacement) details.push(`Placement: ${event.shotPlacement}`);
  if (event.isWicket) {
    const wicketParts = [];
    if (event.wicketType) wicketParts.push(formatWicketTypeLabel(event.wicketType));
    if (event.wicketLocation && shouldShowWicketFieldingDetails(event)) {
      wicketParts.push(event.wicketLocation);
    }
    if (event.wicketFielder && shouldShowWicketFieldingDetails(event)) {
      wicketParts.push(`fielder ${resolvePlayerName(event.wicketFielder) || event.wicketFielder}`);
    }
    if (wicketParts.length > 0) {
      details.push(`Wicket: ${wicketParts.join(' • ')}`);
    }
  }
  return details.join(' | ');
};

const formatWicketSummary = (event, resolvePlayerName) => {
  if (!event?.isWicket) return 'No';
  const parts = [];
  if (event.wicketType) parts.push(formatWicketTypeLabel(event.wicketType));
  if (event.wicketLocation && shouldShowWicketFieldingDetails(event)) {
    parts.push(event.wicketLocation);
  }
  if (event.wicketFielder && shouldShowWicketFieldingDetails(event)) {
    parts.push(`fielder: ${resolvePlayerName(event.wicketFielder) || event.wicketFielder}`);
  }
  return parts.length > 0 ? parts.join(' • ') : 'Yes';
};

const buildCurrentOverFromEvents = (events = [], ballsInOver = 0) => {
  if (events.length === 0) return null;

  const totalBallsInOver = Number(ballsInOver);
  if (!Number.isFinite(totalBallsInOver)) return null;

  let countedBalls = 0;
  const currentOverEvents = [];

  for (let index = events.length - 1; index >= 0; index -= 1) {
    const event = events[index];
    if (event?.countBall && countedBalls === totalBallsInOver) {
      break;
    }
    currentOverEvents.unshift(event);
    if (event?.countBall) {
      countedBalls += 1;
    }
  }

  return {
    ballsInOver: totalBallsInOver,
    remaining: 6 - totalBallsInOver,
    events: currentOverEvents,
  };
};

const buildBowlerBallCounts = (events = []) => {
  const counts = new Map();
  events.forEach((event) => {
    if (!event?.countBall) return;
    const bowlerId = event?.bowler || '';
    if (!bowlerId) return;
    counts.set(bowlerId, (counts.get(bowlerId) || 0) + 1);
  });
  return counts;
};

const getLastCompletedOverBowler = (events = []) => {
  let countedBalls = 0;
  let lastBowler = '';
  events.forEach((event) => {
    if (!event?.countBall) return;
    countedBalls += 1;
    if (countedBalls % 6 === 0) {
      lastBowler = event?.bowler || lastBowler;
    }
  });
  return lastBowler;
};


const getBowlerQuotaRestrictionMeta = ({ bowlerBallCounts, maxOversPerPlayer, maxPlayersAtMaxOvers }) => {
  if (!Number.isFinite(maxOversPerPlayer) || maxOversPerPlayer <= 0) {
    return { fullQuotaCount: 0, maxBallsPerBowler: null, isRestrictionActive: false };
  }
  if (!Number.isFinite(maxPlayersAtMaxOvers) || maxPlayersAtMaxOvers <= 0) {
    return { fullQuotaCount: 0, maxBallsPerBowler: maxOversPerPlayer * 6, isRestrictionActive: false };
  }

  const maxBallsPerBowler = maxOversPerPlayer * 6;
  const fullQuotaCount = [...bowlerBallCounts.values()].filter((balls) => balls >= maxBallsPerBowler).length;
  return {
    fullQuotaCount,
    maxBallsPerBowler,
    isRestrictionActive: true,
  };
};

const wouldBowlerExceedFullQuotaCap = ({
  playerId,
  bowlerBallCounts,
  maxOversPerPlayer,
  maxPlayersAtMaxOvers,
  projectedLegalBallCount = 0,
}) => {
  const { fullQuotaCount, maxBallsPerBowler, isRestrictionActive } =
    getBowlerQuotaRestrictionMeta({
      bowlerBallCounts,
      maxOversPerPlayer,
      maxPlayersAtMaxOvers,
    });

  if (!isRestrictionActive || !maxBallsPerBowler || !playerId) return false;

  const currentBalls = bowlerBallCounts.get(playerId) || 0;
  const projectedBalls = currentBalls + projectedLegalBallCount;

  return (
    currentBalls < maxBallsPerBowler &&
    projectedBalls >= maxBallsPerBowler &&
    fullQuotaCount >= maxPlayersAtMaxOvers
  );
};

const normalizeName = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : '');

const addScorerCandidate = (candidates, value) => {
  if (value === undefined || value === null || value === '') return;
  if (Array.isArray(value)) {
    value.forEach((entry) => addScorerCandidate(candidates, entry));
    return;
  }
  candidates.push(value);
};

const collectScorerCandidates = (...sources) => {
  const candidates = [];
  sources.filter(Boolean).forEach((source) => {
    addScorerCandidate(candidates, source.scorer);
    addScorerCandidate(candidates, source.scorerId);
    addScorerCandidate(candidates, source.assignedScorer);
    addScorerCandidate(candidates, source.assignedScorerId);
    addScorerCandidate(candidates, source.assignedScorers);
  });
  return candidates;
};

const scorerCandidateMatchesAliases = (candidate, aliases) => {
  if (!candidate || !aliases?.size) return false;
  if (typeof candidate === 'string' || typeof candidate === 'number') {
    return aliases.has(normalizeName(String(candidate)));
  }
  if (typeof candidate !== 'object') return false;
  return [
    candidate._id,
    candidate.id,
    candidate.userId,
    candidate.user,
    candidate.playerId,
    candidate.player,
    candidate.scorerId,
    candidate.assignedScorerId,
    candidate.value,
    candidate.name,
    candidate.fullName,
    candidate.playerName,
    candidate.scorerName,
    candidate.email,
    candidate.mobile,
    candidate.phone,
    candidate.label,
    candidate.user?.name,
    candidate.user?.email,
    candidate.user?.mobile,
    candidate.player?.name,
    candidate.player?.fullName,
    candidate.player?.email,
    candidate.player?.mobile,
    candidate.personal?.fullName,
    candidate.personal?.email,
    candidate.personal?.mobile,
  ].some((value) => aliases.has(normalizeName(String(value || ''))));
};
const isAnnouncedStatus = (status) => {
  const normalizedStatus = String(status || '').trim().toLowerCase();
  return normalizedStatus === 'playing' || normalizedStatus === 'substitute';
};
const buildGroupedPlayerOptions = (players = []) => {
  const rows = Array.isArray(players) ? players : [];
  const playingPlayers = rows.filter((player) => String(player?.status || '').trim().toLowerCase() === 'playing');
  const substitutePlayers = rows.filter((player) => String(player?.status || '').trim().toLowerCase() === 'substitute');

  return [
    ...(playingPlayers.length > 0 ? [{ isLabel: true, label: '--- Playing Players ---' }] : []),
    ...playingPlayers,
    ...(substitutePlayers.length > 0 ? [{ isLabel: true, label: '--- Substitute Players ---' }] : []),
    ...substitutePlayers,
  ];
};
const normalizeRosterPlayerId = (value) => {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number') return `${value}`;
  if (!value || typeof value !== 'object') return '';
  const directCandidate = value.id || value._id || value.playerId || value.player || value.userId || value.user || value.value;
  if (directCandidate && directCandidate !== value) return normalizeRosterPlayerId(directCandidate);
  if (typeof value.toString === 'function') {
    const stringified = value.toString().trim();
    if (stringified && stringified !== '[object Object]') return stringified;
  }
  return '';
};
const normalizePlayerId = (player) => normalizeRosterPlayerId(player);

const mergeScoringSelections = ({
  score = {},
  current = {},
  battingRoster = [],
  bowlingRoster = [],
  normalizeRosterValue,
  needsNewBowler = false,
  previousOverBowler = '',
  selectedBowler = '',
  clearStriker = false,
  clearNonStriker = false,
  clearBowler = false,
} = {}) => {
  const normalizeValue = typeof normalizeRosterValue === 'function'
    ? normalizeRosterValue
    : (value) => normalizePlayerId(value);
  const authoritativeStriker = normalizeValue(score?.striker || '', battingRoster);
  const authoritativeNonStriker = normalizeValue(score?.nonStriker || '', battingRoster);
  const authoritativeBowler = normalizeValue(score?.bowler || '', bowlingRoster);
  const localStriker = normalizeValue(current?.striker || '', battingRoster);
  const localNonStriker = normalizeValue(current?.nonStriker || '', battingRoster);
  const localBowler = normalizeValue(current?.bowler || '', bowlingRoster);
  const payloadBowler = normalizeValue(selectedBowler || '', bowlingRoster);
  const normalizedPreviousOverBowler = normalizeValue(previousOverBowler || '', bowlingRoster);

  const nextStriker = clearStriker ? '' : (authoritativeStriker || localStriker || '');
  const nextNonStriker = clearNonStriker ? '' : (authoritativeNonStriker || localNonStriker || '');
  let nextBowler = clearBowler ? '' : (authoritativeBowler || payloadBowler || localBowler || '');

  if (needsNewBowler) {
    const candidateNextBowler = payloadBowler || localBowler || authoritativeBowler || '';
    nextBowler = candidateNextBowler && candidateNextBowler !== normalizedPreviousOverBowler
      ? candidateNextBowler
      : '';
  }

  const authoritativeBowlerSide = typeof score?.bowlerSide === 'string' ? score.bowlerSide.trim() : '';
  const localBowlerSide = typeof current?.bowlerSide === 'string' ? current.bowlerSide.trim() : '';
  const nextBowlerSide = nextBowler ? (authoritativeBowlerSide || localBowlerSide || '') : '';

  return {
    striker: nextStriker,
    nonStriker: nextNonStriker,
    bowler: nextBowler,
    bowlerSide: nextBowlerSide,
  };
};
const hydratePlayerFromId = (id, directory) => {
  const normalizedId = normalizePlayerId(id);
  if (!normalizedId) return null;
  const meta = directory?.get?.(normalizedId) || {};
  return {
    id: normalizedId,
    _id: normalizedId,
    name: meta.name || meta.fullName || meta.displayName || normalizedId,
    email: meta.email || '',
  };
};
const buildStatusMap = (items = []) =>
  Object.fromEntries(
    (Array.isArray(items) ? items : [])
      .map((item) => [normalizePlayerId(item?.playerId || item?.id || item?._id), item?.status])
      .filter(([id, status]) => id && status)
  );

// ✅ Custom breadcrumbs example:
// const crumbs = [
//   { label: 'Home', to: '/' },
//   { label: 'Community', to: `/community/${communityId}` },
//   { label: 'Match Manager', to: `/community/${communityId}/match-manager/${managerId}` },
//   { label: 'Manual Match Format' },
// ];
// <PageShell breadcrumbs={crumbs}>...</PageShell>
// <PageShell breadcrumbOverrides={{ tournamentId: tournament?.name || 'Tournament' }}>

const deriveScoreState = (score, prompts = {}) => {
  const rawStatus = score?.status || 'not_started';
  const isSoloLastBatterMode = Boolean(score?.loneBatterActive);
  const totalBalls = Number(score?.balls) || 0;
  const oversLimit = Number(score?.overs);
  const maxBalls = Number.isFinite(oversLimit) && oversLimit > 0 ? oversLimit * 6 : null;
  const oversCompleted = maxBalls !== null && totalBalls >= maxBalls;
  const isCompleted = rawStatus === 'completed' || (!isSoloLastBatterMode && oversCompleted);
  const suppressSoloAllOut = isSoloLastBatterMode && !isCompleted;
  const isAllOut = !suppressSoloAllOut && (Boolean(score?.allOut) || rawStatus === 'all_out' || Boolean(prompts?.isAllOut));
  const effectiveScoreStatus = isCompleted
    ? 'completed'
    : (isAllOut ? 'all_out' : (isSoloLastBatterMode ? 'in_progress' : rawStatus));

  return {
    effectiveScoreStatus,
    isAllOut,
    isCompleted,
    isSoloLastBatterMode,
  };
};

const deriveOpeningSetupRequired = (score, { striker = '', nonStriker = '', bowler = '' } = {}) => {
  const legalBalls = Math.max(Number(score?.balls) || 0, 0);
  if (legalBalls > 0) {
    return false;
  }

  const isSoloLastBatterMode = Boolean(score?.loneBatterActive);
  const effectiveStriker = score?.striker || striker || '';
  const effectiveNonStriker = isSoloLastBatterMode ? '' : (score?.nonStriker || nonStriker || '');
  const effectiveBowler = score?.bowler || bowler || '';

  return isSoloLastBatterMode
    ? Boolean(!effectiveStriker || !effectiveBowler)
    : Boolean(!effectiveStriker || !effectiveNonStriker || !effectiveBowler);
};

const buildLocalScorePrompts = (score, prompts = {}, action = '') => {
  const { isAllOut, isCompleted, isSoloLastBatterMode } = deriveScoreState(score, prompts);
  const latestEvent = Array.isArray(score?.events) && score.events.length > 0 ? score.events[score.events.length - 1] : null;
  const shouldRecomputePromptsFromScore = action === 'ball' || action === 'undo';
  const canRequestNewBatter = !isAllOut && !isCompleted && !isSoloLastBatterMode;
  const scoreStriker = normalizeId(score?.striker) || '';
  const scoreNonStriker = normalizeId(score?.nonStriker) || '';
  let needsNewStriker = false;
  let needsNewNonStriker = false;

  if (shouldRecomputePromptsFromScore && canRequestNewBatter && latestEvent?.isWicket) {
    const eventStriker = normalizeId(latestEvent?.striker) || '';
    const eventNonStriker = normalizeId(latestEvent?.nonStriker) || '';
    if (isRunOutType(latestEvent?.wicketType)) {
      const runOutWho = String(latestEvent?.runOutWho || '').trim().toLowerCase();
      const outPlayer = normalizeId(latestEvent?.outPlayer || latestEvent?.outPlayerId || latestEvent?.wicketPlayerId || '');
      if (runOutWho === 'striker' || runOutWho === 'non-striker') {
        needsNewStriker = runOutWho === 'striker';
        needsNewNonStriker = runOutWho === 'non-striker';
      } else if (outPlayer && (outPlayer === eventStriker || outPlayer === scoreStriker)) {
        needsNewStriker = true;
      } else if (outPlayer && (outPlayer === eventNonStriker || outPlayer === scoreNonStriker)) {
        needsNewNonStriker = true;
      } else if (!scoreStriker && scoreNonStriker) {
        needsNewStriker = true;
      } else if (!scoreNonStriker && scoreStriker) {
        needsNewNonStriker = true;
      } else {
        needsNewStriker = true;
      }
    } else {
      needsNewStriker = true;
    }
  } else if (canRequestNewBatter) {
    needsNewStriker = Boolean(prompts?.needsNewStriker);
    needsNewNonStriker = Boolean(prompts?.needsNewNonStriker);
    if (!needsNewStriker && !needsNewNonStriker && Boolean(prompts?.needsNewBatter)) {
      if (!scoreStriker && scoreNonStriker) {
        needsNewStriker = true;
      } else if (!scoreNonStriker && scoreStriker) {
        needsNewNonStriker = true;
      } else {
        needsNewStriker = true;
      }
    }
  }
  if (canRequestNewBatter) {
    if (!scoreStriker) needsNewStriker = true;
    if (!scoreNonStriker && !isSoloLastBatterMode) needsNewNonStriker = true;
  }
  const needsNewBatter = needsNewStriker || needsNewNonStriker;
  const safeBalls = Number(score?.balls) || 0;
  const isEndOfOver = safeBalls > 0 && safeBalls % 6 === 0;
  const isValidBallEvent = latestEvent?.countBall === true;
  const handleEndOfOver = () => ({
    needsNewBowler: !isAllOut && !isCompleted,
  });
  const needsNewBowler = shouldRecomputePromptsFromScore
    ? Boolean(isValidBallEvent && isEndOfOver && handleEndOfOver().needsNewBowler)
    : Boolean(prompts?.needsNewBowler) && !isAllOut && !isCompleted;

  return {
    needsNewBatter,
    needsNewStriker,
    needsNewNonStriker,
    needsNewBowler,
    isAllOut,
  };
};

/* Regression guard literals retained for source-based Tournament tests:
 * scorePrompts: prompts,
 * currentInnings: Number(score?.currentInnings) || 1,
 */
const logScoreStateSnapshot = (label, score, prompts = {}) => {
  const { effectiveScoreStatus, isAllOut: allOut, isSoloLastBatterMode } = deriveScoreState(score, prompts);
  const setupRequired = deriveOpeningSetupRequired(score);
  const nonStrikerValue = isSoloLastBatterMode ? '' : score?.nonStriker || '';

};


const hasArrayItems = (value) => Array.isArray(value) && value.length > 0;
const hasCompleteGaliRoster = (match = {}) => {
  const config = match?.configuration || {};
  return hasArrayItems(config.playerDirectory || match.playerDirectory) &&
    hasArrayItems(config.team1PlayersData || match.team1PlayersData) &&
    hasArrayItems(config.team2PlayersData || match.team2PlayersData) &&
    hasArrayItems(config.team1SquadPlayers || match.team1SquadPlayers) &&
    hasArrayItems(config.team2SquadPlayers || match.team2SquadPlayers);
};
const captureGaliCanonicalRoster = (match = {}) => {
  const config = match?.configuration || {};
  return {
    playerDirectory: config.playerDirectory || match.playerDirectory || [],
    team1PlayersData: config.team1PlayersData || match.team1PlayersData || [],
    team2PlayersData: config.team2PlayersData || match.team2PlayersData || [],
    team1SquadPlayers: config.team1SquadPlayers || match.team1SquadPlayers || [],
    team2SquadPlayers: config.team2SquadPlayers || match.team2SquadPlayers || [],
    team1SquadPlayersData: config.team1SquadPlayersData || match.team1SquadPlayersData || config.team1PlayersData || match.team1PlayersData || [],
    team2SquadPlayersData: config.team2SquadPlayersData || match.team2SquadPlayersData || config.team2PlayersData || match.team2PlayersData || [],
  };
};
const preserveGaliCanonicalRoster = (match = {}, roster = {}) => ({
  ...match,
  playerDirectory: hasArrayItems(match.playerDirectory) ? match.playerDirectory : roster.playerDirectory,
  team1PlayersData: hasArrayItems(match.team1PlayersData) ? match.team1PlayersData : roster.team1PlayersData,
  team2PlayersData: hasArrayItems(match.team2PlayersData) ? match.team2PlayersData : roster.team2PlayersData,
  team1SquadPlayers: hasArrayItems(match.team1SquadPlayers) ? match.team1SquadPlayers : roster.team1SquadPlayers,
  team2SquadPlayers: hasArrayItems(match.team2SquadPlayers) ? match.team2SquadPlayers : roster.team2SquadPlayers,
  team1SquadPlayersData: hasArrayItems(match.team1SquadPlayersData) ? match.team1SquadPlayersData : roster.team1SquadPlayersData,
  team2SquadPlayersData: hasArrayItems(match.team2SquadPlayersData) ? match.team2SquadPlayersData : roster.team2SquadPlayersData,
  configuration: {
    ...(match.configuration || {}),
    playerDirectory: hasArrayItems(match.configuration?.playerDirectory) ? match.configuration.playerDirectory : roster.playerDirectory,
    team1PlayersData: hasArrayItems(match.configuration?.team1PlayersData) ? match.configuration.team1PlayersData : roster.team1PlayersData,
    team2PlayersData: hasArrayItems(match.configuration?.team2PlayersData) ? match.configuration.team2PlayersData : roster.team2PlayersData,
    team1SquadPlayers: hasArrayItems(match.configuration?.team1SquadPlayers) ? match.configuration.team1SquadPlayers : roster.team1SquadPlayers,
    team2SquadPlayers: hasArrayItems(match.configuration?.team2SquadPlayers) ? match.configuration.team2SquadPlayers : roster.team2SquadPlayers,
    team1SquadPlayersData: hasArrayItems(match.configuration?.team1SquadPlayersData) ? match.configuration.team1SquadPlayersData : roster.team1SquadPlayersData,
    team2SquadPlayersData: hasArrayItems(match.configuration?.team2SquadPlayersData) ? match.configuration.team2SquadPlayersData : roster.team2SquadPlayersData,
  },
});

const MatchStartPage = () => {
  const {
    organizationId,
    communityId: routeCommunityId,
    clubId: routeClubId,
    individualTournamentId,
    tournamentId: routeTournamentId,
    managerId,
    matchManagerId,
    matchId,
  } = useParams();
  const { token, user } = useAuth();
  const location = useLocation();
  const communityId = useMemo(() => getCommunityId(routeCommunityId), [routeCommunityId]);
  const clubId = useMemo(() => getClubId(routeClubId), [routeClubId]);
  const isGaliScoringRoute = location?.pathname?.startsWith('/gali/matches/');
  const tournamentId = isGaliScoringRoute ? 'gali' : routeTournamentId;
  const routeManagerId = managerId || matchManagerId;
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const matchRef = useRef(null);
  useEffect(() => { matchRef.current = match; }, [match]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isStarting, setIsStarting] = useState(false);
  const [isSavingToss, setIsSavingToss] = useState(false);
  const [tossWinnerInput, setTossWinnerInput] = useState('');
  const [tossDecisionInput, setTossDecisionInput] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [pendingMutation, setPendingMutation] = useState(() => pendingScoringMutationStore.get(matchId) || null);
  const [pendingHydrationComplete, setPendingHydrationComplete] = useState(false);
  const pendingMutationRef = useRef(null);
  const recoveryTimerRef = useRef(null);
  const recoveryAttemptRef = useRef(0);
  const [isEnding, setIsEnding] = useState(false);
  const [isStartingSuperOver, setIsStartingSuperOver] = useState(false);
  const mutationBlocked = isUpdating || Boolean(pendingMutation) || !pendingHydrationComplete;
  const recoveryMessage = pendingMutation?.status === SCORING_MUTATION_STATUS.RECOVERY_REQUIRED
    ? (pendingMutation.expired ? 'This pending action is older than the recovery window. Refresh the score and contact support before continuing.' : 'We could not confirm the last action yet. Do not score it again.')
    : pendingMutation
      ? 'Connection interrupted. Checking whether the delivery was recorded…'
      : '';

  useEffect(() => () => {
    if (recoveryTimerRef.current) clearTimeout(recoveryTimerRef.current);
  }, []);
  const [battingTeam, setBattingTeam] = useState('');
  const [bowlingTeam, setBowlingTeam] = useState('');
  const [overs, setOvers] = useState('');
  const [advancedRuns, setAdvancedRuns] = useState('0');
  const [advancedExtraType, setAdvancedExtraType] = useState('');
  const [advancedCountBall, setAdvancedCountBall] = useState(true);
  const [advancedIsWicket, setAdvancedIsWicket] = useState(false);
  const [isJackpotOver, setIsJackpotOver] = useState(false);
  const [powerPlayStartInput, setPowerPlayStartInput] = useState('');
  const [powerPlayEndInput, setPowerPlayEndInput] = useState('');
  const [striker, setStriker] = useState('');
  const [nonStriker, setNonStriker] = useState('');
  const [bowler, setBowler] = useState('');
  const [bowlerSide, setBowlerSide] = useState('');
  const selectionStateRef = useRef({ striker: '', nonStriker: '', bowler: '', bowlerSide: '' });
  const confirmedNextBowlerKeyRef = useRef('');
  const [shotType, setShotType] = useState('');
  const [shotPlacement, setShotPlacement] = useState('');
  const [wicketType, setWicketType] = useState('');
  const [wicketLocation, setWicketLocation] = useState('');
  const [wicketFielder, setWicketFielder] = useState('');
  const [wicketError, setWicketError] = useState('');
  const [runOutWho, setRunOutWho] = useState('');
  const [runOutCompletedRuns, setRunOutCompletedRuns] = useState('');
  const [runOutThrower, setRunOutThrower] = useState('');
  const [runOutWicketHitter, setRunOutWicketHitter] = useState('');
  const [isWicketConfirmOpen, setIsWicketConfirmOpen] = useState(false);
  const [pendingWicketPayload, setPendingWicketPayload] = useState(null);
  const [playerDirectoryPayload, setPlayerDirectoryPayload] = useState({});
  const galiCanonicalRosterRef = useRef(null);
  const galiSetupInitializedRef = useRef({ matchId: '', innings: 0 });
  const [team1ResponsePlayers, setTeam1ResponsePlayers] = useState([]);
  const [team2ResponsePlayers, setTeam2ResponsePlayers] = useState([]);
  const [scorePrompts, setScorePrompts] = useState({
    needsNewBatter: false,
    needsNewStriker: false,
    needsNewNonStriker: false,
    needsNewBowler: false,
    isAllOut: false,
  });
  const routeStateConfiguration = location?.state?.configuration;
  const routeStateMatch = location?.state?.match;
  const matchConfig = useMemo(
    () => ({
      ...(routeStateConfiguration || {}),
      ...(routeStateMatch?.manualMatchSettings || {}),
      ...(routeStateMatch?.configuration || {}),
      ...(match?.manualMatchSettings || {}),
      ...(match?.configuration || {}),
    }),
    [match?.configuration, match?.manualMatchSettings, routeStateConfiguration, routeStateMatch?.configuration, routeStateMatch?.manualMatchSettings]
  );

  const normalizeRosterValue = useCallback((value, roster = []) => {
    const normalizedValue = normalizePlayerId(value);
    if (!normalizedValue) return '';
    const normalizedValueLower = normalizedValue.toLowerCase();
    const direct = roster.find((player) => {
      const candidateIds = [player?.id, player?._id, player?.playerId, player?.value]
        .map(normalizePlayerId)
        .filter(Boolean);
      return candidateIds.some((candidate) => candidate.toLowerCase() === normalizedValueLower);
    });
    if (direct) return normalizePlayerId(direct?.id || direct?._id || direct?.playerId || direct?.value);
    const byAlias = roster.find((player) => (
      [player?.name, player?.playerName, player?.fullName, player?.displayName, player?.email]
        .filter(Boolean)
        .some((candidate) => String(candidate).trim().toLowerCase() === normalizedValueLower)
    ));
    return byAlias ? normalizePlayerId(byAlias?.id || byAlias?._id || byAlias?.playerId || byAlias?.value) : normalizedValue;
  }, []);

  const syncPlayerSelections = useCallback((nextScore, nextBattingRoster = [], nextBowlingRoster = [], options = {}) => {
    const previousOverBowler = normalizeRosterValue(getLastCompletedOverBowler(nextScore?.events || []), nextBowlingRoster);
    const totalLegalBalls = Number(nextScore?.balls) || 0;
    const latestEvent = Array.isArray(nextScore?.events) && nextScore.events.length > 0
      ? nextScore.events[nextScore.events.length - 1]
      : null;
    const awaitingNextBowler = Boolean(
      options.needsNewBowler ||
      (
        totalLegalBalls > 0 &&
        totalLegalBalls % 6 === 0 &&
        latestEvent?.countBall === true &&
        previousOverBowler
      )
    );
    const nextInnings = Number(nextScore?.currentInnings) || 1;
    const currentInnings = Number(selectionStateRef.current?.currentInnings) || nextInnings;
    const currentMatchId = selectionStateRef.current?.matchId || matchId;
    const shouldPreserveCurrentSelections = currentInnings === nextInnings && currentMatchId === matchId;
    const mergedSelections = mergeScoringSelections({
      score: nextScore,
      current: shouldPreserveCurrentSelections ? selectionStateRef.current : {},
      battingRoster: nextBattingRoster,
      bowlingRoster: nextBowlingRoster,
      normalizeRosterValue,
      needsNewBowler: awaitingNextBowler,
      previousOverBowler,
      selectedBowler: options.selectedBowler || '',
      clearStriker: Boolean(options.clearStriker),
      clearNonStriker: Boolean(options.clearNonStriker),
      clearBowler: Boolean(options.clearBowler),
    });

    selectionStateRef.current = { ...mergedSelections, currentInnings: nextInnings, matchId };
    setStriker(mergedSelections.striker);
    setNonStriker(mergedSelections.nonStriker);
    setBowler(mergedSelections.bowler);
    setBowlerSide(mergedSelections.bowlerSide);
  }, [matchId, normalizeRosterValue]);

  const loadMatchState = useCallback(async ({ clearLoading = false } = {}) => {
    if (!token || !tournamentId) return null;

    if (!clearLoading) setIsLoading(true);
    setError('');

    try {
      const matchResponse = await fetchMatchById({ token, tournamentId, matchId });
      const hydratedMatch = matchResponse?.match || null;
      if (!hydratedMatch) {
        throw new Error('Match not found.');
      }

      const nextHydratedMatch = isGaliScoringRoute && galiCanonicalRosterRef.current
        ? preserveGaliCanonicalRoster(hydratedMatch, galiCanonicalRosterRef.current)
        : hydratedMatch;
      if (isGaliScoringRoute && hasCompleteGaliRoster(nextHydratedMatch)) {
        galiCanonicalRosterRef.current = captureGaliCanonicalRoster(nextHydratedMatch);
      }

      const currentVersion = Number(matchRef.current?.score?.scoringStateVersion);
      const nextVersion = Number(nextHydratedMatch?.score?.scoringStateVersion);
      if (!Number.isFinite(currentVersion) || !Number.isFinite(nextVersion) || nextVersion >= currentVersion) {
        setMatch(nextHydratedMatch);
      }
      setTeam1ResponsePlayers(
        (Array.isArray(matchResponse?.team1) ? matchResponse.team1 : []).map((player) => ({
          id: normalizePlayerId(player?.playerId || player?.id || player?._id || player?.value),
          name: player?.name || player?.playerName || player?.fullName || player?.displayName || player?.playerId || player?.id || '',
          email: player?.email || '',
          status: player?.status,
        }))
      );
      setTeam2ResponsePlayers(
        (Array.isArray(matchResponse?.team2) ? matchResponse.team2 : []).map((player) => ({
          id: normalizePlayerId(player?.playerId || player?.id || player?._id || player?.value),
          name: player?.name || player?.playerName || player?.fullName || player?.displayName || player?.playerId || player?.id || '',
          email: player?.email || '',
          status: player?.status,
        }))
      );
      const hydratedConfig = {
        ...(hydratedMatch?.manualMatchSettings || {}),
        ...(hydratedMatch?.configuration || {}),
      };
      setPlayerDirectoryPayload(
        nextHydratedMatch.playerDirectory ||
        {
          ...(hydratedConfig?.playerDirectory || {}),
          ...(matchResponse?.playerDirectory || {}),
        }
      );
      setScorePrompts({
        needsNewBatter: false,
        needsNewStriker: false,
        needsNewNonStriker: false,
        needsNewBowler: false,
        isAllOut: false,
      });

      const team1 = hydratedMatch.team1 || '';
      const team2 = hydratedMatch.team2 || '';
      const defaultOvers = hydratedConfig?.overs || '';
      const tossWinner = hydratedConfig?.tossWinner || '';
      const tossDecision = normalizeTossDecision(hydratedConfig?.tossDecision);
      setTossWinnerInput(tossWinner);
      setTossDecisionInput(tossDecision);
      const computedTeams = computeInningsTeams({
        team1,
        team2,
        tossWinner,
        tossDecision,
      });

      setBattingTeam(
        nextHydratedMatch.score?.battingTeam
          || hydratedConfig?.battingTeam
          || computedTeams.battingTeam
          || team1
      );
      setBowlingTeam(
        nextHydratedMatch.score?.bowlingTeam
          || hydratedConfig?.bowlingTeam
          || computedTeams.bowlingTeam
          || team2
      );
      setOvers(nextHydratedMatch.score?.overs || defaultOvers);

      return nextHydratedMatch;
    } catch (err) {
      setError(err.message || 'Unable to load match details.');
      return null;
    } finally {
      if (!clearLoading) setIsLoading(false);
    }
  }, [isGaliScoringRoute, matchId, token, tournamentId]);
  const userRoles = useMemo(
    () => new Set([...(user?.roles || []), user?.role].filter(Boolean)),
    [user]
  );
  const isSuperAdmin = userRoles.has('super_admin');
  const scorerAliases = useMemo(() => {
    const aliases = new Set();
    [
      user?._id,
      user?.id,
      user?.name,
      user?.fullName,
      user?.email,
      user?.mobile,
      user?.phone,
      user?.playerProfile?._id,
      user?.playerProfile?.id,
      user?.playerProfile?.fullName,
      user?.playerProfile?.email,
      user?.playerProfile?.mobile,
      user?.playerProfile?.phone,
    ].forEach((value) => {
      const normalized = normalizeName(value);
      if (normalized) {
        aliases.add(normalized);
      }
    });
    return aliases;
  }, [user]);

  useEffect(() => {
    loadMatchState();
  }, [loadMatchState]);

  useEffect(() => {
    selectionStateRef.current = { ...selectionStateRef.current, striker, nonStriker, bowler, bowlerSide, matchId };
  }, [striker, nonStriker, bowler, bowlerSide, matchId]);

  useEffect(() => {
    setTossWinnerInput(matchConfig?.tossWinner || '');
    setTossDecisionInput(normalizeTossDecision(matchConfig?.tossDecision));
  }, [matchConfig?.tossDecision, matchConfig?.tossWinner]);

  useEffect(() => {
    if (!wicketType) {
      if (wicketLocation) setWicketLocation('');
      if (wicketFielder) setWicketFielder('');
      if (runOutWho) setRunOutWho('');
      if (runOutCompletedRuns) setRunOutCompletedRuns('');
      if (runOutThrower) setRunOutThrower('');
      if (runOutWicketHitter) setRunOutWicketHitter('');
      return;
    }

    if (!shouldCaptureWicketLocation(wicketType) && wicketLocation) {
      setWicketLocation('');
    }

    if ((!wicketTypesRequiringFielder.has(wicketType) || isRunOutType(wicketType)) && wicketFielder) {
      setWicketFielder('');
    }
    if (!isRunOutType(wicketType)) {
      if (runOutWho) setRunOutWho('');
      if (runOutCompletedRuns) setRunOutCompletedRuns('');
      if (runOutThrower) setRunOutThrower('');
      if (runOutWicketHitter) setRunOutWicketHitter('');
    }
  }, [wicketType, wicketLocation, wicketFielder, runOutCompletedRuns, runOutThrower, runOutWho, runOutWicketHitter]);

  useEffect(() => {
    if (!match) return;
    const computedTeams = computeInningsTeams({
      team1: match.team1,
      team2: match.team2,
      tossWinner: matchConfig?.tossWinner,
      tossDecision: matchConfig?.tossDecision,
    });
    const nextBatting =
      match?.score?.battingTeam
      || matchConfig?.battingTeam
      || computedTeams.battingTeam
      || match.team1
      || '';
    const nextBowling =
      match?.score?.bowlingTeam
      || matchConfig?.bowlingTeam
      || computedTeams.bowlingTeam
      || match.team2
      || '';
    const nextOvers = match?.score?.overs || matchConfig?.overs || '';
    setBattingTeam(nextBatting);
    setBowlingTeam(nextBowling);
    setOvers(nextOvers);
  }, [match, matchConfig?.battingTeam, matchConfig?.bowlingTeam, matchConfig?.overs, matchConfig?.tossDecision, matchConfig?.tossWinner]);

  const matchLabel = useMemo(() => {
    if (!match) return 'Match';
    return `Match ${match.matchNumber || ''}`.trim();
  }, [match]);

  const team1 = match?.team1 || '';
  const team2 = match?.team2 || '';
  const team1SquadIds = useMemo(() => uniqueById([
    ...(Array.isArray(matchConfig?.team1SquadPlayers) ? matchConfig.team1SquadPlayers : []),
    ...(Array.isArray(matchConfig?.team1Players) ? matchConfig.team1Players : []),
    ...(Array.isArray(match?.configuration?.team1SquadPlayers) ? match.configuration.team1SquadPlayers : []),
    ...(Array.isArray(match?.configuration?.team1Players) ? match.configuration.team1Players : []),
    ...(Array.isArray(match?.team1SquadPlayers) ? match.team1SquadPlayers : []),
    ...(Array.isArray(match?.team1Players) ? match.team1Players : []),
  ]).map(normalizePlayerId).filter(Boolean), [match, matchConfig]);
  const team2SquadIds = useMemo(() => uniqueById([
    ...(Array.isArray(matchConfig?.team2SquadPlayers) ? matchConfig.team2SquadPlayers : []),
    ...(Array.isArray(matchConfig?.team2Players) ? matchConfig.team2Players : []),
    ...(Array.isArray(match?.configuration?.team2SquadPlayers) ? match.configuration.team2SquadPlayers : []),
    ...(Array.isArray(match?.configuration?.team2Players) ? match.configuration.team2Players : []),
    ...(Array.isArray(match?.team2SquadPlayers) ? match.team2SquadPlayers : []),
    ...(Array.isArray(match?.team2Players) ? match.team2Players : []),
  ]).map(normalizePlayerId).filter(Boolean), [match, matchConfig]);
  const playerDirectory = useMemo(() => {
    const configuredPlayers = [
      ...(Array.isArray(matchConfig?.team1SquadPlayers) ? matchConfig.team1SquadPlayers : []),
      ...(Array.isArray(matchConfig?.team1Players) ? matchConfig.team1Players : []),
      ...(Array.isArray(match?.configuration?.team1SquadPlayers) ? match.configuration.team1SquadPlayers : []),
      ...(Array.isArray(match?.configuration?.team1Players) ? match.configuration.team1Players : []),
      ...(Array.isArray(match?.team1SquadPlayers) ? match.team1SquadPlayers : []),
      ...(Array.isArray(match?.team1Players) ? match.team1Players : []),
      ...(Array.isArray(matchConfig?.team2SquadPlayers) ? matchConfig.team2SquadPlayers : []),
      ...(Array.isArray(matchConfig?.team2Players) ? matchConfig.team2Players : []),
      ...(Array.isArray(match?.configuration?.team2SquadPlayers) ? match.configuration.team2SquadPlayers : []),
      ...(Array.isArray(match?.configuration?.team2Players) ? match.configuration.team2Players : []),
      ...(Array.isArray(match?.team2SquadPlayers) ? match.team2SquadPlayers : []),
      ...(Array.isArray(match?.team2Players) ? match.team2Players : []),
      matchConfig?.team1Captain,
      matchConfig?.team1ViceCaptain,
      matchConfig?.team1WicketKeeper,
      matchConfig?.team2Captain,
      matchConfig?.team2ViceCaptain,
      matchConfig?.team2WicketKeeper,
    ];
    const fallbackPlayers = uniqueById([
      ...configuredPlayers,
      ...team1SquadIds,
      ...team2SquadIds,
    ]).map((value) => {
      if (value && typeof value === 'object') return value;
      return { id: normalizeId(value) };
    }).filter((player) => normalizeId(player));
    return buildPlayerDirectory({
      players: fallbackPlayers,
      directory: playerDirectoryPayload,
      teamData: [
        ...(Array.isArray(matchConfig?.team1PlayersData) ? matchConfig.team1PlayersData : []),
        ...(Array.isArray(matchConfig?.team2PlayersData) ? matchConfig.team2PlayersData : []),
        ...(Array.isArray(matchConfig?.team1SquadPlayersData) ? matchConfig.team1SquadPlayersData : []),
        ...(Array.isArray(matchConfig?.team2SquadPlayersData) ? matchConfig.team2SquadPlayersData : []),
        ...(Array.isArray(match?.team1PlayersData) ? match.team1PlayersData : []),
        ...(Array.isArray(match?.team2PlayersData) ? match.team2PlayersData : []),
        ...(Array.isArray(match?.team1SquadPlayersData) ? match.team1SquadPlayersData : []),
        ...(Array.isArray(match?.team2SquadPlayersData) ? match.team2SquadPlayersData : []),
      ],
    });
  }, [team1SquadIds, team2SquadIds, matchConfig, match, playerDirectoryPayload]);
  const team1StatusById = useMemo(
    () => buildStatusMap(
      match?.configuration?.team1MatchPlayers ||
      match?.team1MatchPlayers ||
      []
    ),
    [match?.configuration?.team1MatchPlayers, match?.team1MatchPlayers]
  );
  const team2StatusById = useMemo(
    () => buildStatusMap(
      match?.configuration?.team2MatchPlayers ||
      match?.team2MatchPlayers ||
      []
    ),
    [match?.configuration?.team2MatchPlayers, match?.team2MatchPlayers]
  );
  const team1Rows = useMemo(() => {
    if (team1ResponsePlayers.length) {
      return team1ResponsePlayers
        .map((player) => ({
          id: normalizePlayerId(player?.id),
          name: player?.name || player?.id || '',
          status: player?.status || team1StatusById[normalizePlayerId(player?.id)] || 'unannounced',
        }))
        .filter((player) => player.id);
    }
    return team1SquadIds
      .map((id) => {
        const player = hydratePlayerFromId(id, playerDirectory);
        if (!player) return null;
        return {
          ...player,
          id: normalizePlayerId(player?.id),
          status: team1StatusById[id] || 'unannounced',
        };
      })
      .filter(Boolean);
  }, [playerDirectory, team1ResponsePlayers, team1SquadIds, team1StatusById]);
  const team2Rows = useMemo(() => {
    if (team2ResponsePlayers.length) {
      return team2ResponsePlayers
        .map((player) => ({
          id: normalizePlayerId(player?.id),
          name: player?.name || player?.id || '',
          status: player?.status || team2StatusById[normalizePlayerId(player?.id)] || 'unannounced',
        }))
        .filter((player) => player.id);
    }
    return team2SquadIds
      .map((id) => {
        const player = hydratePlayerFromId(id, playerDirectory);
        if (!player) return null;
        return {
          ...player,
          id: normalizePlayerId(player?.id),
          status: team2StatusById[id] || 'unannounced',
        };
      })
      .filter(Boolean);
  }, [playerDirectory, team2ResponsePlayers, team2SquadIds, team2StatusById]);
  const getAnnouncedPlayersForTeamName = useCallback(
    (teamName) => {
      const rows = teamName === team1 ? team1Rows : teamName === team2 ? team2Rows : [];
      return rows.filter((player) => isAnnouncedStatus(player?.status));
    },
    [team1, team1Rows, team2, team2Rows]
  );

  const resolvePlayerName = (playerId) => {
    const normalizedPlayerId = normalizeId(playerId);
    if (!normalizedPlayerId) return '';
    return playerDirectory.get(normalizedPlayerId)?.name || normalizedPlayerId;
  };

  const isAssignedScorer = useMemo(() => {
    if (!scorerAliases.size) return false;
    return collectScorerCandidates(match, matchConfig, match?.configuration).some((candidate) =>
      scorerCandidateMatchesAliases(candidate, scorerAliases)
    );
  }, [match, matchConfig, scorerAliases]);
  const isTournamentAdmin = useMemo(() => isSuperAdmin || userRoles.has('tournament_admin'), [isSuperAdmin, userRoles]);
  const isIndividualTournamentManager = useMemo(
    () =>
      Boolean(individualTournamentId) &&
      (userRoles.has('individual') ||
        userRoles.has('individual_admin') ||
        userRoles.has('match_manager') ||
        userRoles.has('tournament_creator')),
    [individualTournamentId, userRoles]
  );
  const isAuthorizedScorer = isTournamentAdmin || isAssignedScorer || isIndividualTournamentManager;

  const score = match?.score || null;
  const hasAuthoritativeCurrentOver = Boolean(score?.currentOver);
  const authoritativeJackpotOver = Boolean(
    hasAuthoritativeCurrentOver ? score?.currentOver?.jackpotOver : score?.currentOverJackpot
  );
  const canToggleJackpotOver = Boolean(
    hasAuthoritativeCurrentOver
      ? score?.currentOver?.canToggleJackpot
      : ((Number(score?.currentOver?.ballsInOver) || 0) === 0)
  );
  const isJackpotCheckboxChecked = Boolean(isJackpotOver);
  const isJackpotOverActive = Boolean(isJackpotCheckboxChecked || authoritativeJackpotOver);
  const showBowlerJackpotBadge = Boolean(isJackpotOverActive);
  const derivedState = deriveScoreState(score, scorePrompts);
  const isSoloLastBatterMode = derivedState.isSoloLastBatterMode;
  const effectiveScoreStatus = derivedState.effectiveScoreStatus;
  const isAllOut = derivedState.isAllOut;
  const isCompleted = derivedState.isCompleted;
  const bowlingTeamName = score?.bowlingTeam || bowlingTeam;

  const events = score?.events || [];
  const firstBallBowled = hasFirstBallBowled(match);
  const tossCompleted = isTossCompleted(match);
  const startDestination = getMatchStartDestination(match);
  const isTossViewRequested = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('view') === 'toss';
  }, [location.search]);
  const derivedState1 = deriveScoreState(score, scorePrompts);
  const isSolo = derivedState1.isSoloLastBatterMode;
  const showScoringDesk = tossCompleted && effectiveScoreStatus !== 'not_started' && startDestination === 'scoring';
  const isTossLocked = firstBallBowled;
  const showTossSection = (!showScoringDesk || isTossViewRequested) && !isTossLocked;
  const showScoringSection = showScoringDesk && (!isTossViewRequested || isTossLocked);

  const isMobileViewport = typeof window !== 'undefined' && window.innerWidth < 768;
  const matchStarted = effectiveScoreStatus !== 'not_started';
  const showMobileTossOnly = isMobileViewport && !tossCompleted && !matchStarted;

  useEffect(() => {
    if (showMobileTossOnly) {
      return;
    }
  }, [isMobileViewport, showMobileTossOnly, tossCompleted]);
  const isTossValid = Boolean(tossWinnerInput) && Boolean(normalizeTossDecision(tossDecisionInput));
  const canStart =
    isTossValid &&
    !isLoading &&
    !isTossLocked;
  const bowlerBallCounts = useMemo(() => buildBowlerBallCounts(events), [events]);
  const lastOverBowler = useMemo(() => getLastCompletedOverBowler(events), [events]);
  const maxOversPerPlayer = Number(matchConfig?.maxOversPerPlayer);
  const maxPlayersAtMaxOvers = Number(matchConfig?.maxPlayersAtMaxOvers);
  const isNewOver = Boolean(score?.balls && Number(score?.balls) % 6 === 0);
  const totalBalls = Number(score?.balls) || 0;
  const ballsInOver = totalBalls % 6;
  const remainingBallsInOver = ballsInOver === 0 ? 6 : 6 - ballsInOver;
  const displayOvers = useMemo(() => {
    const baseOvers = oversFromBalls(score?.balls);
    if (!score?.balls || !isNewOver) return baseOvers;
    if (!bowler || !lastOverBowler || bowler === lastOverBowler) return baseOvers;
    const completedOvers = Math.floor(totalBalls / 6);
    return `${completedOvers}.0`;
  }, [bowler, isNewOver, lastOverBowler, score?.balls, totalBalls]);
  const oversLimit = Number(score?.overs);
  const maxBalls = Number.isFinite(oversLimit) && oversLimit > 0 ? oversLimit * 6 : null;
  const isOversComplete = maxBalls !== null && totalBalls >= maxBalls;
  const configuredPlayersPerSide = Number(matchConfig?.playingCount ?? matchConfig?.playersPerSide ?? 11);
  const rosterCount = getAnnouncedPlayersForTeamName(battingTeam).length;
  const hasConfiguredPlayers = Number.isFinite(configuredPlayersPerSide) && configuredPlayersPerSide > 0;
  const effectivePlayers = hasConfiguredPlayers
    ? Math.min(configuredPlayersPerSide, rosterCount || configuredPlayersPerSide)
    : rosterCount;
  const allowLastBatsman = Boolean(matchConfig?.allowLastBatsman);
  const maxWickets = effectivePlayers > 1 ? (allowLastBatsman ? effectivePlayers : effectivePlayers - 1) : null;
  const wicketCount = Number(score?.wickets) || 0;
  const isWicketsComplete = maxWickets !== null && wicketCount >= maxWickets;
  const currentOverKey = `${matchId}:${Number(score?.currentInnings) || 1}:${totalBalls}`;
  const previousOverBowler = lastOverBowler || '';
  const bowlerToHideForThisOver = previousOverBowler;
  const localBowler = bowler || '';
  const backendBowler = score?.bowler || '';
  const currentOverBowler = localBowler || backendBowler || '';
  const hasConfirmedNextBowlerForCurrentOver = confirmedNextBowlerKeyRef.current === currentOverKey;
  const hasValidNextOverBowler = Boolean(localBowler && previousOverBowler && localBowler !== previousOverBowler);
  const isWaitingForNextBowler = Boolean(
    !hasConfirmedNextBowlerForCurrentOver &&
    isNewOver &&
    previousOverBowler &&
    !isOversComplete &&
    !isWicketsComplete &&
    effectiveScoreStatus === 'in_progress' &&
    !hasValidNextOverBowler
  );
  const needsNewBowlerSelection = Boolean(
    (scorePrompts.needsNewBowler || isWaitingForNextBowler) &&
    !hasConfirmedNextBowlerForCurrentOver &&
    !hasValidNextOverBowler
  );
  const configuredPowerPlayStart = Number(matchConfig?.powerPlayStartOver);
  const configuredPowerPlayEnd = Number(matchConfig?.powerPlayEndOver);
  const fallbackPowerPlay = useMemo(
    () =>
      Number.isFinite(configuredPowerPlayStart) && configuredPowerPlayStart > 0
        ? [
            {
              startOver: configuredPowerPlayStart,
              endOver:
                Number.isFinite(configuredPowerPlayEnd) && configuredPowerPlayEnd > 0
                  ? configuredPowerPlayEnd
                  : configuredPowerPlayStart,
            },
          ]
        : [],
    [configuredPowerPlayEnd, configuredPowerPlayStart]
  );
  const powerPlays = useMemo(() => {
    const saved = normalizePowerPlays(score?.powerPlays);
    return saved.length ? saved : fallbackPowerPlay;
  }, [fallbackPowerPlay, score?.powerPlays]);
  const safeBalls = Number(totalBalls) || 0;

  const currentOverNumber =
    safeBalls === 0
      ? 1
      : Math.floor((safeBalls - 1) / 6) + 1;

  const hasPowerPlay = Array.isArray(powerPlays) && powerPlays.length > 0;

  const isPowerPlayOver =
    hasPowerPlay &&
    powerPlays.some(
      (entry) =>
        currentOverNumber >= entry.startOver && currentOverNumber <= entry.endOver
    );
  const handleStartMatch = async () => {
    if (!token || !tournamentId || !matchId) return;
    if (!isTossValid || isTossLocked) {
      return;
    }
    const normalizedDecision = normalizeTossDecision(tossDecisionInput);
    if (!tossWinnerInput || !normalizedDecision) {
      setError('Select toss winner and decision before starting the match.');
      return;
    }
    setIsSavingToss(true);
    setIsStarting(true);
    setError('');

    try {
      const configurationPayload = {
        ...(matchConfig || {}),
        tossWinner: tossWinnerInput,
        tossDecision: normalizedDecision,
      };
      if (!isGaliScoringRoute) {
        await updateMatchConfiguration({
          token,
          tournamentId,
          matchId,
          payload: configurationPayload,
        });
      }
      const response = await startMatch({
        token,
        tournamentId,
        matchId,
        payload: {
          battingTeam,
          bowlingTeam,
          overs,
          tossWinner: tossWinnerInput,
          tossDecision: normalizedDecision,
        },
      });
      setMatch(response.match);
      if (scoringDeskPath) {
        navigate(scoringDeskPath);
      }
    } catch (err) {
      setError(err.message || 'Unable to start match.');
    } finally {
      setIsSavingToss(false);
      setIsStarting(false);
    }
  };

  const handleScoreUpdate = async (payload, { isRecovery = false } = {}) => {
    if (!token || !tournamentId || !matchId) return;
    if (isUpdating && !isRecovery) return null;
    if (pendingMutationRef.current && !isRecovery) return null;

    logScoreStateSnapshot('local-state-before-merge', match?.score, scorePrompts);

    if (!isRecovery && payload?.action === 'ball') {
      if (needsNewBowlerSelection) {
        setError('Select the new bowler before scoring this over.');
        return null;
      }
      const setupRequired = deriveOpeningSetupRequired(score, { striker, nonStriker, bowler });
      if (setupRequired && !isSolo) {
        setError(isSoloBatterActive ? 'Select the striker and bowler before scoring a delivery.' : 'Select the striker, non-striker, and bowler before scoring a delivery.');
        return null;
      }
      const freshDerived = deriveScoreState(score, scorePrompts);

      if (!hasRequiredPlayers && !freshDerived.isSoloLastBatterMode) {
        setError(isSoloBatterActive ? 'Select the striker and bowler before scoring a delivery.' : 'Select the striker, non-striker, and bowler before scoring a delivery.');
        return null;
      }
      if (isAllOut && !isSoloLastBatterMode) {
        setError('Wicket limit reached. The innings is complete.');
        return null;
      }
      if (effectiveScoreStatus === 'completed') {
        setError('Overs limit reached. The innings is complete.');
        return null;
      }
    }
    const normalizedPayload = payload?.action === 'ball' && isRetiredOutWicketType(payload?.wicketType)
      ? { ...payload, countBall: false }
      : { ...payload };


    const mutationPayload = {
      ...normalizedPayload,
      requestId: normalizedPayload?.requestId || createScoringRequestId(),
      expectedStateVersion: normalizedPayload?.expectedStateVersion !== undefined
        ? Number(normalizedPayload.expectedStateVersion)
        : (Number.isInteger(Number(score?.scoringStateVersion)) ? Number(score.scoringStateVersion) : 0),
    };
    if (!isRecovery && normalizedPayload?.action === 'undo') {
      const latestEvent = Array.isArray(score?.events) ? score.events[score.events.length - 1] : null;
      if (latestEvent?.deliveryIndex !== undefined) mutationPayload.expectedLatestDeliveryIndex = latestEvent.deliveryIndex;
      if (latestEvent?._id) mutationPayload.expectedLatestEventId = latestEvent._id;
    }
    if (!isRecovery) {
      const pending = {
        requestId: mutationPayload.requestId,
        actionType: mutationPayload.action || 'score',
        payload: { ...mutationPayload },
        expectedStateVersion: mutationPayload.expectedStateVersion,
        matchId,
        tournamentId,
        createdAt: Date.now(),
        status: SCORING_MUTATION_STATUS.SUBMITTING,
        retryCount: 0,
        lastAttemptAt: Date.now(),
      };
      pendingScoringMutationStore.set(matchId, pending);
      pendingMutationRef.current = pending;
      setPendingMutation(pending);
    } else if (pendingMutationRef.current) {
      const nextPending = { ...pendingMutationRef.current, status: SCORING_MUTATION_STATUS.RETRYING, lastAttemptAt: Date.now() };
      pendingScoringMutationStore.set(matchId, nextPending);
      pendingMutationRef.current = nextPending;
      setPendingMutation(nextPending);
    }
    setIsUpdating(true);
    setError('');

    let pendingPersisted = isRecovery;
    try {
      const didPersist = savePendingScoringMutation({ ...pendingMutationRef.current, status: SCORING_MUTATION_STATUS.SUBMITTING });
      pendingPersisted = pendingPersisted || didPersist;
      if (!pendingPersisted) throw new Error('Pending scoring storage unavailable.');
      const response = await updateMatchScore({
        token,
        tournamentId,
        matchId,
        payload: mutationPayload,
      });

      if (response?.match) {
        const responseVersion = Number(response.match?.score?.scoringStateVersion);
        const currentVersion = Number(matchRef.current?.score?.scoringStateVersion);
        if (!Number.isFinite(currentVersion) || !Number.isFinite(responseVersion) || responseVersion >= currentVersion) {
          setMatch(response.match);
        }
      }

      const refreshedMatch = await loadMatchState({ clearLoading: true });
      const authoritativeMatch = refreshedMatch || response?.match || null;
      const authoritativeScore = authoritativeMatch?.score || null;
      const refreshedPrompts = buildLocalScorePrompts(
        authoritativeScore,
        {},
        normalizedPayload?.action || ''
      );
      const selectedPayloadBowler = normalizeRosterValue(normalizedPayload?.bowler || '', getAnnouncedPlayersForTeamName(authoritativeScore?.bowlingTeam || bowlingTeamName));
      const refreshedPreviousOverBowler = normalizeRosterValue(
        getLastCompletedOverBowler(authoritativeScore?.events || []),
        getAnnouncedPlayersForTeamName(authoritativeScore?.bowlingTeam || bowlingTeamName)
      );
      const hasSavedNextBowler = Boolean(
        normalizedPayload?.action === 'players' &&
        selectedPayloadBowler &&
        selectedPayloadBowler !== refreshedPreviousOverBowler
      );
      const effectiveRefreshedPrompts = hasSavedNextBowler
        ? { ...refreshedPrompts, needsNewBowler: false }
        : refreshedPrompts;
      if (hasSavedNextBowler) {
        confirmedNextBowlerKeyRef.current = `${matchId}:${Number(authoritativeScore?.currentInnings) || 1}:${Number(authoritativeScore?.balls) || 0}`;
      }
      setScorePrompts(effectiveRefreshedPrompts);

      const nextBattingAnnouncedPlayers = getAnnouncedPlayersForTeamName(authoritativeScore?.battingTeam || battingTeam);
      const nextBowlingAnnouncedPlayers = getAnnouncedPlayersForTeamName(authoritativeScore?.bowlingTeam || bowlingTeamName);
      syncPlayerSelections(authoritativeScore, nextBattingAnnouncedPlayers, nextBowlingAnnouncedPlayers, {
        needsNewBowler: effectiveRefreshedPrompts.needsNewBowler,
        selectedBowler: hasSavedNextBowler ? selectedPayloadBowler : '',
      });
      logScoreStateSnapshot('authoritative-state-after-refresh', authoritativeScore, effectiveRefreshedPrompts);
      // 🔥 FORCE fresh derived state immediately
      const freshDerived = deriveScoreState(authoritativeScore, effectiveRefreshedPrompts);

// clear stale UI errors
      if (freshDerived.effectiveScoreStatus !== 'completed') {
        setError('');
      }

      if (normalizedPayload?.action === 'undo') {
        const fresh = deriveScoreState(authoritativeScore, refreshedPrompts);

        if (!fresh.isCompleted && !fresh.isAllOut) {
          setError('');

          syncPlayerSelections(
            authoritativeScore,
            getAnnouncedPlayersForTeamName(authoritativeScore?.battingTeam || battingTeam),
            getAnnouncedPlayersForTeamName(authoritativeScore?.bowlingTeam || bowlingTeamName),
            { needsNewBowler: refreshedPrompts.needsNewBowler }
          );
        }
      }

      pendingScoringMutationStore.delete(matchId);
      removePendingScoringMutation(matchId);
      pendingMutationRef.current = null;
      setPendingMutation(null);
      recoveryAttemptRef.current = 0;
      return response;
    } catch (err) {
      if (!pendingPersisted) {
        pendingScoringMutationStore.delete(matchId);
        pendingMutationRef.current = null;
        setPendingMutation(null);
        setError('Unable to prepare scoring recovery. Please try again.');
        return null;
      }
      if (err?.code === 'SCORE_STATE_CHANGED') {
        pendingScoringMutationStore.delete(matchId);
        removePendingScoringMutation(matchId);
        pendingMutationRef.current = null;
        setPendingMutation(null);
        recoveryAttemptRef.current = 0;
        await loadMatchState({ clearLoading: true });
        setError('The score changed on another device. The latest state was loaded.');
        return null;
      }
      const classification = classifyScoringMutationError(err);
      if (classification === 'unknownOutcome' || classification === 'inProgress') {
        const currentPending = pendingMutationRef.current || {
          requestId: mutationPayload.requestId,
          actionType: mutationPayload.action || 'score',
          payload: { ...mutationPayload },
          expectedStateVersion: mutationPayload.expectedStateVersion,
          matchId,
          tournamentId,
          createdAt: Date.now(),
          retryCount: 0,
        };
        const nextAttempt = recoveryAttemptRef.current + 1;
        recoveryAttemptRef.current = nextAttempt;
        const nextStatus = nextAttempt > 4 ? SCORING_MUTATION_STATUS.RECOVERY_REQUIRED : SCORING_MUTATION_STATUS.OUTCOME_UNKNOWN;
        const nextPending = { ...currentPending, status: nextStatus, retryCount: nextAttempt, lastAttemptAt: Date.now() };
        pendingScoringMutationStore.set(matchId, nextPending);
        savePendingScoringMutation(nextPending);
        pendingMutationRef.current = nextPending;
        setPendingMutation(nextPending);
        setError('');
        if (nextAttempt <= 4) {
          if (recoveryTimerRef.current) clearTimeout(recoveryTimerRef.current);
          recoveryTimerRef.current = setTimeout(() => {
            setPendingMutation((current) => current ? { ...current, status: SCORING_MUTATION_STATUS.CHECKING_OUTCOME } : current);
            handleScoreUpdate(nextPending.payload, { isRecovery: true });
          }, recoveryDelayMs(nextAttempt - 1));
        }
        return null;
      }
      pendingScoringMutationStore.delete(matchId);
      removePendingScoringMutation(matchId);
      pendingMutationRef.current = null;
      setPendingMutation(null);
      setError(err.message || 'Unable to update score.');
      return null;
    } finally {
      setIsUpdating(false);
    }
  };

  const checkPendingMutation = () => {
    const pending = pendingMutationRef.current;
    if (!pending) return;
    recoveryAttemptRef.current = Math.max(0, Number(pending.retryCount || 0) - 1);
    handleScoreUpdate(pending.payload, { isRecovery: true });
  };

  const refreshDuringRecovery = async () => {
    await loadMatchState({ clearLoading: true });
  };

  useEffect(() => {
    const stored = listPendingScoringMutations().find((entry) => String(entry.matchId) === String(matchId));
    if (stored && !pendingMutationRef.current) {
      const recovered = isPendingMutationExpired(stored)
        ? { ...stored, status: SCORING_MUTATION_STATUS.RECOVERY_REQUIRED, expired: true }
        : stored;
      pendingScoringMutationStore.set(matchId, recovered);
      pendingMutationRef.current = recovered;
      setPendingMutation(recovered);
      recoveryAttemptRef.current = Math.max(0, Number(recovered.retryCount || 0) - 1);
      if (!recovered.expired) recoveryTimerRef.current = setTimeout(() => handleScoreUpdate(recovered.payload, { isRecovery: true }), 300);
    }
    setPendingHydrationComplete(true);
    return () => {
      if (recoveryTimerRef.current) clearTimeout(recoveryTimerRef.current);
    };
  }, [matchId]);

  const handleAdvancedExtraChange = (event) => {
    const value = event.target.value;
    const noBallExtras = ['wide', 'no-ball', 'penalty', 'jackpot-plus', 'jackpot-minus'];
    setAdvancedExtraType(value);
    setAdvancedCountBall(!noBallExtras.includes(value));
  };

  const handleAdvancedSubmit = async () => {
    const setupRequired = deriveOpeningSetupRequired(score, { striker, nonStriker, bowler });
    if ((setupRequired && !isSolo) || !hasRequiredPlayers || needsNewBowlerSelection) {
      setError(isSoloBatterActive ? 'Select the striker and bowler before scoring a delivery.' : 'Select the striker, non-striker, and bowler before scoring a delivery.');
      return;
    }
    if (
      wouldBowlerExceedFullQuotaCap({
        playerId: bowler,
        bowlerBallCounts,
        maxOversPerPlayer,
        maxPlayersAtMaxOvers,
        projectedLegalBallCount: advancedCountBall ? 1 : 0,
      })
    ) {
      setError(`Only ${maxPlayersAtMaxOvers} bowlers can complete the full ${maxOversPerPlayer}-over quota in this match.`);
      return;
    }
    const parsedRuns = Number(advancedRuns);
    const payload = {
      action: 'ball',
      runs: Number.isFinite(parsedRuns) ? parsedRuns : 0,
      isWicket: advancedIsWicket,
      countBall: advancedCountBall,
      allowNegativeRuns: true,
    };

    if (advancedExtraType) {
      payload.extraType = advancedExtraType;
    }

    const enrichedPayload = withBallMetadata(payload);
    if (payload.isWicket) {
      openWicketConfirmModal(enrichedPayload);
      return;
    }
    const response = await handleScoreUpdate(enrichedPayload);
    if (response && payload.isWicket) {
      resetWicketDetails();
    }
    setAdvancedIsWicket(false);
    setShotType('');
    setShotPlacement('');
  };

  const handlePlayersSave = () => {
    const activeBowler = needsNewBowlerSelection
      ? normalizeRosterValue(bowler || '', bowlingAnnouncedPlayers)
      : (
        normalizeRosterValue(bowler || '', bowlingAnnouncedPlayers)
        || normalizeRosterValue(score?.bowler || '', bowlingAnnouncedPlayers)
        || ''
      );
    if (!isSoloLastBatterMode && striker && nonStriker && striker === nonStriker) {
      setError('Striker and non-striker must be different players.');
      return;
    }
    if (needsNewBowlerSelection && (!activeBowler || activeBowler === previousOverBowler)) {
      setError('Select a new bowler for the next over.');
      return;
    }
    if (activeBowler && (activeBowler === striker || (!isSoloLastBatterMode && activeBowler === nonStriker))) {
      setError('Bowler must be different from striker and non-striker.');
      return;
    }
    const projectedLegalBallCount = needsNewBowlerSelection
      ? remainingBallsInOver
      : 0;

    if (
      wouldBowlerExceedFullQuotaCap({
        playerId: activeBowler,
        bowlerBallCounts,
        maxOversPerPlayer,
        maxPlayersAtMaxOvers,
        projectedLegalBallCount,
      })
    ) {
      setError(`Only ${maxPlayersAtMaxOvers} bowlers can complete the full ${maxOversPerPlayer}-over quota in this match.`);
      return;
    }

    handleScoreUpdate({
      action: 'players',
      striker,
      nonStriker,
      bowler: activeBowler,
      bowlerSide,
    });
  };

  const handleAddPowerPlay = async () => {
    const startOver = Number(powerPlayStartInput);
    const endOver = powerPlayEndInput ? Number(powerPlayEndInput) : startOver;
    if (!Number.isFinite(startOver) || startOver <= 0) {
      setError('Power play start over must be a positive number.');
      return;
    }
    if (!Number.isFinite(endOver) || endOver <= 0) {
      setError('Power play end over must be a positive number.');
      return;
    }
    if (endOver < startOver) {
      setError('Power play end over must be greater than or equal to start over.');
      return;
    }
    const nextPowerPlays = [...powerPlays, { startOver, endOver }];
    const response = await handleScoreUpdate({
      action: 'power_play',
      powerPlays: nextPowerPlays,
    });
    if (response) {
      setPowerPlayStartInput('');
      setPowerPlayEndInput('');
    }
  };

  const handleRemovePowerPlay = (index) => {
    const nextPowerPlays = powerPlays.filter((_, entryIndex) => entryIndex !== index);
    handleScoreUpdate({
      action: 'power_play',
      powerPlays: nextPowerPlays,
    });
  };

  const handleStrikerChange = (event) => {
    const value = event.target.value;
    selectionStateRef.current = { ...selectionStateRef.current, striker: value };
    setStriker(value);
  };

  const handleNonStrikerChange = (event) => {
    const value = event.target.value;
    selectionStateRef.current = { ...selectionStateRef.current, nonStriker: value };
    setNonStriker(value);
  };

  const handleBowlerChange = (event) => {
    const value = event.target.value;
    if (needsNewBowlerSelection && previousOverBowler && value === previousOverBowler) {
      setError('Previous over bowler cannot bowl consecutive overs.');
      selectionStateRef.current = { ...selectionStateRef.current, bowler: '' };
      setBowler('');
      return;
    }
    setError('');
    selectionStateRef.current = { ...selectionStateRef.current, bowler: value };
    setBowler(value);
  };

  const handleBowlerSideChange = (event) => {
    const value = event.target.value;
    selectionStateRef.current = { ...selectionStateRef.current, bowlerSide: value };
    setBowlerSide(value);
  };

  const handleJackpotOverToggle = async (event) => {
    if (!canToggleJackpotOver) {
      event.preventDefault();
      return;
    }
    const nextValue = event.target.checked;
    setIsJackpotOver(nextValue);
    console.log('JACKPOT_INDICATOR_STATE_DEBUG', {
      totalBalls,
      ballsInOver,
      isJackpotCheckboxChecked: nextValue,
      isJackpotOverActive: Boolean(nextValue || authoritativeJackpotOver),
      showJackpotIndicator: Boolean(nextValue || authoritativeJackpotOver),
      source: 'isJackpotOver checkbox state + authoritativeJackpotOver fallback; not submitted ball events/currentOverBallChips/currentOverEvents',
      trigger: 'checkbox-toggle',
    });
    const response = await handleScoreUpdate({
      action: 'set_current_over_jackpot',
      jackpotOver: nextValue,
    });
    if (!response) {
      setIsJackpotOver(authoritativeJackpotOver);
    }
  };

  const withBallMetadata = (payload) => ({
    ...payload,
    jackpotOver: payload?.action === 'ball' ? authoritativeJackpotOver : payload?.jackpotOver,
    striker,
    nonStriker,
    bowler: selectedBowler,
    bowlerSide,
    shotType,
    shotPlacement,
  });

  const resetWicketDetails = () => {
    setWicketType('');
    setWicketLocation('');
    setWicketFielder('');
    setRunOutWho('');
    setRunOutCompletedRuns('');
    setRunOutThrower('');
    setRunOutWicketHitter('');
    setWicketError('');
  };

  const addWicketDetails = (payload) => {
    if (!payload.isWicket) return payload;
    if (!wicketType) {
      setWicketError('Select how the batter got out.');
      return null;
    }
    if (isRunOutType(wicketType)) {
      const selectedRunNumber = Number(runOutCompletedRuns);
      const safeSelectedRunNumber = Number.isFinite(selectedRunNumber) ? selectedRunNumber : 0;
      const completedRuns = safeSelectedRunNumber > 0 ? safeSelectedRunNumber - 1 : 0;
      if (!runOutWho) {
        setWicketError('Select who got run out.');
        return null;
      }
      if (runOutCompletedRuns === '') {
        setWicketError('Select at which run the run out happened.');
        return null;
      }
      if (!runOutThrower) {
        setWicketError('Select the thrower for the run out.');
        return null;
      }
      if (!runOutWicketHitter) {
        setWicketError('Select who hit the wicket in the run out.');
        return null;
      }
      setWicketError('');
      return {
        ...payload,
        wicketType: 'run-out',
        runOutWho,
        runs: completedRuns,
        completedRuns,
        runOutCompletedRuns: completedRuns,
        wicketFielder: runOutThrower,
        thrower: runOutThrower,
        wicketHitter: runOutWicketHitter,
        runOutAtRunNumber: safeSelectedRunNumber,
        outPlayer: runOutWho === 'striker' ? striker : nonStriker,
        countBall: true,
      };
    }
    const needsLocation = shouldCaptureWicketLocation(wicketType);
    if (needsLocation && !wicketLocation) {
      setWicketError('Select where the wicket fell.');
      return null;
    }
    if (wicketTypesRequiringFielder.has(wicketType) && !wicketFielder) {
      setWicketError('Select the fielder involved in the dismissal.');
      return null;
    }

    setWicketError('');
    return {
      ...payload,
      countBall: isRetiredOutWicketType(wicketType) ? false : payload?.countBall,
      wicketType,
      wicketLocation: wicketLocation || undefined,
      wicketFielder: wicketFielder || undefined,
    };
  };

  const closeWicketConfirmModal = () => {
    if (isUpdating) return;
    setIsWicketConfirmOpen(false);
    setPendingWicketPayload(null);
  };

  const openWicketConfirmModal = (payload) => {
    const enrichedPayload = addWicketDetails(payload);
    if (!enrichedPayload) return;
    const wicketPayloadSnapshot = { ...enrichedPayload };
    setPendingWicketPayload(wicketPayloadSnapshot);
    setIsWicketConfirmOpen(true);
  };

  const confirmPendingWicket = async () => {
    if (!pendingWicketPayload || isUpdating) return;
    const wicketPayloadSnapshot = { ...pendingWicketPayload };
    const response = await handleScoreUpdate(wicketPayloadSnapshot);
    if (response) {
      resetWicketDetails();
      setAdvancedIsWicket(false);
      setShotType('');
      setShotPlacement('');
      closeWicketConfirmModal();
    }
  };

  const pendingWicketSummary = useMemo(() => {
    if (!pendingWicketPayload?.isWicket) return [];
    const summary = [
      { label: 'Batter', value: resolvePlayerName(pendingWicketPayload.outPlayer) || resolvePlayerName(pendingWicketPayload.striker) || 'N/A' },
      { label: 'Bowler', value: resolvePlayerName(pendingWicketPayload.bowler) || 'N/A' },
      { label: 'Wicket type', value: formatWicketTypeLabel(pendingWicketPayload.wicketType) || 'N/A' },
      { label: 'Location', value: pendingWicketPayload.wicketLocation || 'N/A' },
      { label: 'Fielder', value: resolvePlayerName(pendingWicketPayload.wicketFielder) || 'N/A' },
    ];
    if (isRunOutType(pendingWicketPayload.wicketType)) {
      summary.push(
        { label: 'Run-out batter', value: pendingWicketPayload.runOutWho === 'non-striker' ? 'Non-striker' : 'Striker' },
        { label: 'Completed runs', value: `${Number(pendingWicketPayload.completedRuns ?? 0)}` },
        { label: 'Thrower', value: resolvePlayerName(pendingWicketPayload.thrower || pendingWicketPayload.wicketFielder) || 'N/A' },
        { label: 'Wicket hitter', value: resolvePlayerName(pendingWicketPayload.wicketHitter) || 'N/A' },
      );
    }

    if (pendingWicketPayload.shotType) {
      summary.push({ label: 'Shot', value: pendingWicketPayload.shotType });
    }

    if (pendingWicketPayload.shotPlacement) {
      summary.push({ label: 'Placement', value: pendingWicketPayload.shotPlacement });
    }

    return summary;
  }, [pendingWicketPayload, resolvePlayerName]);


  useEffect(() => {
    setIsJackpotOver(authoritativeJackpotOver);
  }, [authoritativeJackpotOver]);

  useEffect(() => {
    if (!wicketError) return;
    if (isRunOutType(wicketType)) {
      if (runOutWho && runOutCompletedRuns !== '' && runOutThrower && runOutWicketHitter) setWicketError('');
      return;
    }
    const needsFielder = wicketTypesRequiringFielder.has(wicketType);
    const needsLocation = shouldCaptureWicketLocation(wicketType);
    const hasLocation = !needsLocation || wicketLocation;
    if (wicketType && hasLocation && (!needsFielder || wicketFielder)) {
      setWicketError('');
    }
  }, [wicketError, wicketType, wicketLocation, wicketFielder, runOutWho, runOutCompletedRuns, runOutThrower, runOutWicketHitter]);

  const fullQuotaRestrictionMessage =
    Number.isFinite(maxOversPerPlayer) && maxOversPerPlayer > 0 && Number.isFinite(maxPlayersAtMaxOvers) && maxPlayersAtMaxOvers > 0
      ? `Only ${maxPlayersAtMaxOvers} bowlers can bowl the full ${maxOversPerPlayer}-over quota.`
      : '';
  const isBowlerEligibleForCurrentPhase = useCallback((playerId) => {
    if (!playerId) return false;
    if (Number.isFinite(maxOversPerPlayer) && maxOversPerPlayer > 0) {
      const maxBalls = maxOversPerPlayer * 6;
      const bowlerBalls = bowlerBallCounts.get(playerId) || 0;
      if (bowlerBalls >= maxBalls) return false;
      if (bowlerBalls + remainingBallsInOver > maxBalls) return false;
    }
    if (
      wouldBowlerExceedFullQuotaCap({
        playerId,
        bowlerBallCounts,
        maxOversPerPlayer,
        maxPlayersAtMaxOvers,
        projectedLegalBallCount: isNewOver ? remainingBallsInOver : 1,
      })
    ) {
      return false;
    }
    if (isNewOver && lastOverBowler && playerId === lastOverBowler) {
      return false;
    }
    return true;
  }, [bowlerBallCounts, isNewOver, lastOverBowler, maxOversPerPlayer, maxPlayersAtMaxOvers, remainingBallsInOver]);

  const battingTeamRows = useMemo(
    () => (battingTeam === team1 ? team1Rows : battingTeam === team2 ? team2Rows : []),
    [battingTeam, team1, team1Rows, team2, team2Rows]
  );
  const bowlingTeamRows = useMemo(
    () => (bowlingTeamName === team1 ? team1Rows : bowlingTeamName === team2 ? team2Rows : []),
    [bowlingTeamName, team1, team1Rows, team2, team2Rows]
  );
  const battingAnnouncedPlayers = useMemo(
    () =>
      battingTeamRows.filter((player) => {
        const status = String(player?.status || '').trim().toLowerCase();
        return status === 'playing' || status === 'substitute';
      }),
    [battingTeamRows]
  );
  const bowlingAnnouncedPlayers = useMemo(
    () =>
      bowlingTeamRows.filter((player) => {
        const status = String(player?.status || '').trim().toLowerCase();
        return status === 'playing' || status === 'substitute';
      }),
    [bowlingTeamRows]
  );
  const teamsReady = Boolean(team1 && team2);
  const selectorDataReady = teamsReady && (team1Rows.length > 0 || team2Rows.length > 0);
  const isGaliRosterHydrated = !isGaliScoringRoute || (
    playerDirectory.size > 0 &&
    battingAnnouncedPlayers.length > 0 &&
    bowlingAnnouncedPlayers.length > 0
  );

  useEffect(() => {
    if (!score) return;
    if (isGaliScoringRoute && !isGaliRosterHydrated) return;
    const setupInnings = Number(score?.currentInnings) || 1;
    if (isGaliScoringRoute) {
      const initialized = galiSetupInitializedRef.current;
      if (initialized.matchId === matchId && initialized.innings === setupInnings) return;
      galiSetupInitializedRef.current = { matchId, innings: setupInnings };
    }
    syncPlayerSelections(
      score,
      getAnnouncedPlayersForTeamName(score?.battingTeam || battingTeam),
      getAnnouncedPlayersForTeamName(score?.bowlingTeam || bowlingTeamName),
      { needsNewBowler: scorePrompts.needsNewBowler }
    );
  }, [battingTeam, bowlingTeamName, getAnnouncedPlayersForTeamName, isGaliRosterHydrated, isGaliScoringRoute, matchId, score, scorePrompts.needsNewBowler, syncPlayerSelections]);
  const allBattingPlayers = battingAnnouncedPlayers;
  const dismissedPlayerIds = useMemo(() => buildDismissedPlayerIds(score), [score]);
  const { strikerOptions, nonStrikerOptions } = useMemo(
    () => buildBatterDropdownOptions({
      allBattingPlayers,
      dismissedPlayerIds,
      striker: striker || score?.striker || '',
      nonStriker: nonStriker || score?.nonStriker || '',
    }),
    [allBattingPlayers, dismissedPlayerIds, nonStriker, score?.nonStriker, score?.striker, striker]
  );
  const strikerSelectorOptions = strikerOptions;
  const nonStrikerSelectorOptions = nonStrikerOptions;

  console.log('BATTER_DROPDOWN_FILTER_DEBUG', {
    allBattingPlayers,
    dismissedPlayerIds,
    strikerOptions,
    nonStrikerOptions
  });
  const bowlerSelectorOptions = useMemo(
    () => buildBowlerDropdownOptions({ bowlingPlayers: bowlingAnnouncedPlayers, bowlerToHideForThisOver }),
    [bowlerToHideForThisOver, bowlingAnnouncedPlayers]
  );

  console.log('FRONTEND_BOWLER_FILTER_DEBUG', {
    totalBalls,
    ballsInOver,
    currentOver: currentOverNumber,
    currentOverBowler,
    previousOverBowler,
    bowlerToHideForThisOver,
    bowlerOptionsBeforeFilter: bowlingAnnouncedPlayers,
    bowlerOptionsAfterFilter: bowlerSelectorOptions,
  });
  const groupedStrikerSelectorOptions = useMemo(
    () => buildGroupedPlayerOptions(strikerSelectorOptions),
    [strikerSelectorOptions]
  );
  const groupedNonStrikerSelectorOptions = useMemo(
    () => buildGroupedPlayerOptions(nonStrikerSelectorOptions),
    [nonStrikerSelectorOptions]
  );
  const groupedBowlerSelectorOptions = useMemo(
    () => buildGroupedPlayerOptions(bowlerSelectorOptions),
    [bowlerSelectorOptions]
  );

  useEffect(() => {
    const selectedStrikerId = normalizePlayerId(striker);
    const selectedNonStrikerId = normalizePlayerId(nonStriker);
    const shouldClearStriker = Boolean(selectedStrikerId && dismissedPlayerIds.has(selectedStrikerId));
    const shouldClearNonStriker = Boolean(
      selectedNonStrikerId &&
      (dismissedPlayerIds.has(selectedNonStrikerId) || selectedNonStrikerId === selectedStrikerId)
    );
    if (!shouldClearStriker && !shouldClearNonStriker) return;

    const nextStriker = shouldClearStriker ? '' : striker;
    const nextNonStriker = shouldClearNonStriker ? '' : nonStriker;
    selectionStateRef.current = {
      ...selectionStateRef.current,
      striker: nextStriker,
      nonStriker: nextNonStriker,
    };
    if (shouldClearStriker) setStriker('');
    if (shouldClearNonStriker) setNonStriker('');
  }, [dismissedPlayerIds, nonStriker, striker]);

  useEffect(() => {
    if (!selectorDataReady) {
      return;
    }
  }, [
    battingAnnouncedPlayers,
    bowlerSelectorOptions,
    bowlingAnnouncedPlayers,
    match?.configuration?.team1MatchPlayers,
    match?.configuration?.team2MatchPlayers,
    match?.team1MatchPlayers,
    match?.team2MatchPlayers,
    nonStrikerSelectorOptions,
    selectorDataReady,
    strikerSelectorOptions,
    team1Rows,
    team1StatusById,
    team2Rows,
    team2StatusById,
  ]);

  const { fullQuotaCount, maxBallsPerBowler, isRestrictionActive: isBowlerQuotaRestrictionActive } = useMemo(
    () => getBowlerQuotaRestrictionMeta({ bowlerBallCounts, maxOversPerPlayer, maxPlayersAtMaxOvers }),
    [bowlerBallCounts, maxOversPerPlayer, maxPlayersAtMaxOvers]
  );

  const isSoloBatterActive = isSoloLastBatterMode;
  const shouldKeepInningsLiveForSoloBatter =
    isSoloLastBatterMode && effectiveScoreStatus === 'in_progress';
  const effectiveNonStriker = isSoloLastBatterMode ? '' : (score?.nonStriker || nonStriker);
  const selectedBowler = needsNewBowlerSelection
    ? (localBowler && localBowler !== previousOverBowler ? localBowler : '')
    : (localBowler || backendBowler || '');
  const hasRequiredPlayers = Boolean((score?.striker || striker) && selectedBowler && (effectiveNonStriker || isSoloLastBatterMode));

  const teams = [match?.team1, match?.team2].filter(Boolean);
  const otherTeam = (team) => teams.find((entry) => entry !== team) || '';
  const tossWinner = matchConfig?.tossWinner || '';
  const tossDecision = matchConfig?.tossDecision || '';
  const tossSummary =
    tossWinner && tossDecision
      ? `${tossWinner} won the toss and chose to ${tossDecision === 'bat' ? 'bat' : 'field'}.`
      : '';
  const scoreExtras = score?.extras || {};
  const runOutRunOptions = useMemo(() => ([
    { value: '0', label: 'Before 1st run (0)' },
    { value: '1', label: 'During 1st run (1)' },
    { value: '2', label: 'During 2nd run (2)' },
    { value: '3', label: 'During 3rd run (3)' },
  ]), []);
  const completedInnings = Array.isArray(score?.innings) ? score.innings : [];
  const sortedCompletedInnings = useMemo(
    () => [...completedInnings].sort((a, b) => Number(a?.number) - Number(b?.number)),
    [completedInnings]
  );
  const recentTieInnings = sortedCompletedInnings.slice(-2);
  const isTieScore = recentTieInnings.length === 2 &&
    Number(recentTieInnings[0]?.runs || 0) === Number(recentTieInnings[1]?.runs || 0);
  const currentInnings = Number(score?.currentInnings) || (completedInnings.length ? completedInnings.length + 1 : 1);
  const isInningsComplete = isAllOut || isCompleted;
  const isMatchComplete =
    Boolean(score?.matchComplete) ||
    (Number(score?.currentInnings) >= 2 &&
      (effectiveScoreStatus === 'completed' || effectiveScoreStatus === 'all_out') &&
      !isTieScore);
  const isAwaitingNextInnings = currentInnings === 1 && isInningsComplete && !isMatchComplete && !shouldKeepInningsLiveForSoloBatter;
  const currentOverCompleted = needsNewBowlerSelection && effectiveScoreStatus === 'in_progress';
  const needsNewBowler = currentOverCompleted;
  const currentBowlerBalls = selectedBowler ? (bowlerBallCounts.get(selectedBowler) || 0) : 0;
  const bowlerQuotaBlocked =
    needsNewBowler &&
    Boolean(selectedBowler) &&
    Number.isFinite(maxBallsPerBowler) &&
    maxBallsPerBowler > 0 &&
    isBowlerQuotaRestrictionActive &&
    currentBowlerBalls < maxBallsPerBowler &&
    currentBowlerBalls + remainingBallsInOver >= maxBallsPerBowler &&
    fullQuotaCount >= maxPlayersAtMaxOvers;
  const scoringLocked =
    (scorePrompts.needsNewBatter && !isSoloLastBatterMode) ||
    needsNewBowlerSelection ||
    (isAllOut && !isSoloLastBatterMode) ||
    effectiveScoreStatus === 'completed' ||
    isMatchComplete ||
    !hasRequiredPlayers ||
    Boolean(pendingMutation);
  const eventCount = Number(score?.eventCount ?? score?.events?.length ?? 0);
  const statsLocked = Boolean(match?.statsLocked || (score?.matchComplete && score?.statsAppliedAt));
  const canUndo =
    eventCount > 0 &&
    score?.status !== 'not_started' &&
    !Boolean(score?.matchComplete) &&
    !statsLocked;

  useEffect(() => {
    if (!needsNewBowlerSelection) return;
    if (!bowler && !bowlerSide) return;
    if (localBowler && localBowler !== previousOverBowler) return;
    selectionStateRef.current = { ...selectionStateRef.current, bowler: '', bowlerSide: '' };
    setBowler('');
    setBowlerSide('');
  }, [bowler, bowlerSide, localBowler, needsNewBowlerSelection, previousOverBowler]);

  useEffect(() => {
    if (!bowler) return;
    if (!selectorDataReady || bowlerSelectorOptions.length === 0) return;
    if (normalizeRosterValue(bowler, bowlerSelectorOptions) === bowler) return;
    selectionStateRef.current = { ...selectionStateRef.current, bowler: '', bowlerSide: '' };
    setBowler('');
    setBowlerSide('');
  }, [bowler, bowlerSelectorOptions, normalizeRosterValue, selectorDataReady]);

  const handleSwapStrikers = () => {
    if (!striker || !nonStriker) return;
    selectionStateRef.current = { ...selectionStateRef.current, striker: nonStriker, nonStriker: striker };
    setStriker(nonStriker);
    setNonStriker(striker);
  };
  const derivedCurrentOver = useMemo(
    () => buildCurrentOverFromEvents(score?.events || [], ballsInOver),
    [ballsInOver, score?.events]
  );
  const defaultOver = { events: [], ballsInOver, remaining: 6 - ballsInOver };
  const currentOver =
    score?.currentOver?.events?.length > 0
      ? score.currentOver
      : derivedCurrentOver || score?.currentOver || defaultOver;
  const currentOverEvents = currentOver.events || [];
  const displayOver = currentOver;
  const displayOverEvents = displayOver?.events || [];
  const currentOverLegalEvents = useMemo(
    () => displayOverEvents.filter((event) => event?.countBall === true),
    [displayOverEvents]
  );
  useEffect(() => {
    currentOverEvents.forEach((event) => {
    });
  }, [currentOverEvents]);
  const currentOverRows = useMemo(() => {
    let ballNumber = 0;
    return currentOverLegalEvents.map((event) => {
      if (!event?.countBall) return null;
      ballNumber += 1;
      const label = `${ballNumber}`;
      return { label, event };
    }).filter(Boolean);
  }, [currentOverLegalEvents]);
  const currentOverEventLabels = useMemo(
    () => currentOverEvents.map((event) => formatBallDisplay(event)),
    [currentOverEvents]
  );
  const currentOverBallChips = useMemo(
    () => currentOverRows.map(({ event }) => formatBallDisplay(event)),
    [currentOverRows]
  );
  useEffect(() => {
    console.log('JACKPOT_INDICATOR_STATE_DEBUG', {
      totalBalls,
      ballsInOver,
      isJackpotCheckboxChecked,
      isJackpotOverActive,
      showJackpotIndicator: showBowlerJackpotBadge,
      source: 'isJackpotOver checkbox state + authoritativeJackpotOver fallback; not submitted ball events/currentOverBallChips/currentOverEvents',
      currentOverBallChipsCount: currentOverBallChips.length,
      currentOverEventsCount: currentOverEvents.length,
      trigger: 'state-effect',
    });
  }, [ballsInOver, currentOverBallChips.length, currentOverEvents.length, isJackpotCheckboxChecked, isJackpotOverActive, showBowlerJackpotBadge, totalBalls]);
  const getRecentBallTitle = (event) => formatBallDetails(event, resolvePlayerName);
  const assignedMatchManagerId = normalizeId(match?.matchManager) || normalizeId(match?.matchManagerId) || normalizeId(match?.assignedMatchManager);
  const currentUserId = normalizeId(user?._id) || normalizeId(user?.id);
  const resolvedManagerId = individualTournamentId
    ? assignedMatchManagerId || normalizeId(routeManagerId) || currentUserId
    : normalizeId(routeManagerId);
  const matchManagerPathParams = {
    organizationId,
    communityId,
    clubId,
    individualTournamentId,
    managerId: resolvedManagerId,
    tournamentId,
    matchId,
  };
  const isIndividualView = !organizationId && !communityId && !clubId && !routeManagerId;
  const schedulePath =
    buildMatchManagerTournamentPath(matchManagerPathParams) ||
    (isIndividualView && tournamentId ? `/individual/tournaments/${tournamentId}/matches` : '/');
  const matchStartPath = buildMatchManagerMatchStartPath(matchManagerPathParams);
  const individualTossPath = buildMatchManagerMatchTossPath(matchManagerPathParams);
  const scoreboardPath = matchStartPath
    ? matchStartPath.replace(/\/start$/, '/scoreboard')
    : tournamentId && matchId
      ? `/public/tournaments/${tournamentId}/matches/${matchId}`
      : '';
  const tossPath = individualTournamentId
    ? individualTossPath
    : matchStartPath || (tournamentId && matchId ? `/dashboard/individual/tournaments/${tournamentId}/matches/${matchId}/start` : '');
  const scoringDeskPath = individualTournamentId ? matchStartPath : tossPath;
  const tossOnlyPath = tossPath
    ? individualTournamentId
      ? tossPath
      : `${tossPath}?view=toss`
    : '';

  useEffect(() => {
    if (!match || !tossPath) return;
    if (!tossCompleted && !isTossViewRequested && !isTossLocked) {
      navigate(tossOnlyPath, { replace: true });
      return;
    }
    if (!individualTournamentId && tossCompleted && isTossViewRequested && scoringDeskPath) {
      navigate(scoringDeskPath, { replace: true });
    }
  }, [individualTournamentId, isTossLocked, isTossViewRequested, match, navigate, scoringDeskPath, tossCompleted, tossOnlyPath, tossPath]);

  if (!token) {
    return <Navigate to="/login" replace />;
  }
  if (!isLoading && match && !isAuthorizedScorer) {
    return <Navigate to={schedulePath || '/'} replace />;
  }

  return (
    <PageShell containerClassName="p-0">
    <div className="match-start-page py-4">
      {!showMobileTossOnly && (
      <div className="match-start-desktop-header mb-4 d-flex flex-column gap-2">
        <p className="eyebrow text-uppercase text-primary fw-semibold mb-0">Match scoring</p>
        <h1 className="h3 mb-1">{showScoringSection ? `${matchLabel} scoring desk` : `${matchLabel} toss & start`}</h1>
        <p className="text-muted mb-0">
          {showScoringSection
            ? 'Keep ball-by-ball scoring synced for your teams.'
            : 'Capture toss details, then start the match when ready.'}
        </p>
        {schedulePath && schedulePath !== '/' && (
          <div className="d-flex gap-2 flex-wrap">
            <Link
              className="btn btn-sm btn-outline-secondary"
              to={schedulePath}
            >
              Back to match schedule
            </Link>
            {scoreboardPath && (
              <Link
                className="btn btn-sm btn-outline-primary"
                to={scoreboardPath}
              >
                View scoreboard
              </Link>
            )}
            {showScoringSection && !firstBallBowled && tossOnlyPath && (
              <Link className="btn btn-sm btn-outline-warning" to={tossOnlyPath}>
                Go back to Toss
              </Link>
            )}
          </div>
        )}
      </div>
      )}

      {!showMobileTossOnly && (error || (!match && !isLoading)) && (
        <div className="alert alert-danger" role="alert">
          {error || 'Match details are unavailable.'}
        </div>
      )}

      {!showMobileTossOnly && pendingMutation && (
        <div className="alert alert-warning d-flex align-items-start gap-2" role="alert" data-testid="scoring-recovery-banner">
          <div className="flex-grow-1">
            <div className="fw-semibold" data-testid="scoring-recovery-status">
              {pendingMutation.status === SCORING_MUTATION_STATUS.CHECKING_OUTCOME
                ? 'Checking the latest score…'
                : recoveryMessage}
            </div>
            <div className="small mt-1">Scoring controls are temporarily locked to prevent a duplicate delivery.</div>
            {pendingMutation.status === SCORING_MUTATION_STATUS.RECOVERY_REQUIRED && (
              <div className="d-flex gap-2 mt-2">
                <button type="button" className="btn btn-sm btn-warning" onClick={checkPendingMutation} data-testid="scoring-recovery-check-again">Check Again</button>
                <button type="button" className="btn btn-sm btn-outline-warning" onClick={refreshDuringRecovery} data-testid="scoring-recovery-refresh">Refresh Score</button>
              </div>
            )}
          </div>
        </div>
      )}

      {!showMobileTossOnly && isMatchComplete && (
        <div className="alert alert-info" role="alert">
          This match has been ended. Stats are locked and no further scoring updates are allowed.
        </div>
      )}

      {!showMobileTossOnly && isLoading && (
        <div className="alert alert-secondary" role="alert">
          Loading match details...
        </div>
      )}

      {match && isGaliScoringRoute && !isGaliRosterHydrated && !showMobileTossOnly && (
        <div className="alert alert-secondary" role="alert">
          Loading Gali player rosters...
        </div>
      )}

      {match && (!isGaliScoringRoute || isGaliRosterHydrated) && !showMobileTossOnly && (
        <div className="match-start-match-card card shadow-sm border-0 mb-4">
          <div className="card-body d-flex flex-column flex-lg-row justify-content-between gap-3">
            <div>
              <h2 className="h5 mb-1">{match.team1} vs {match.team2}</h2>
              <div className="d-flex align-items-center gap-3 mb-1">
                <div className="d-flex align-items-center gap-2">
                  {match.team1Logo ? <img src={match.team1Logo} alt={match.team1 || 'Team 1'} className="rounded border" style={{ width: 28, height: 28, objectFit: 'cover' }} /> : null}
                  <span className="small">{match.team1 || 'Team 1'}</span>
                </div>
                <span className="text-muted small">vs</span>
                <div className="d-flex align-items-center gap-2">
                  {match.team2Logo ? <img src={match.team2Logo} alt={match.team2 || 'Team 2'} className="rounded border" style={{ width: 28, height: 28, objectFit: 'cover' }} /> : null}
                  <span className="small">{match.team2 || 'Team 2'}</span>
                </div>
              </div>
              <p className="text-muted mb-0">
                {match.dayLabel || `Day ${match.day}`} • {match.time}
              </p>
              {tossSummary ? (
                <p className="text-muted mb-0">{tossSummary}</p>
              ) : null}
            </div>
            <div className="d-flex align-items-center gap-2">
                <span className="badge bg-primary-subtle text-primary-emphasis">
                  {isMatchComplete
                    ? 'Completed'
                    : isAwaitingNextInnings
                      ? 'Innings break'
                      : effectiveScoreStatus === 'all_out'
                      ? 'All out'
                      : effectiveScoreStatus === 'completed'
                        ? 'Completed'
                        : effectiveScoreStatus === 'in_progress'
                          ? 'Live'
                          : 'Not started'}
                </span>
              {score?.lastUpdated && (
                <span className="text-muted small">Updated {new Date(score.lastUpdated).toLocaleString()}</span>
              )}
            </div>
          </div>
        </div>
      )}

      {showTossSection && match && (
        <div className="card shadow-sm border-0 mb-4">
          <div className="card-body">
            <div className="d-flex flex-column gap-3">
              <div className="d-flex justify-content-between align-items-center">
                <h2 className="h5 mb-0">Toss</h2>
              </div>
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <label className="form-label">Toss winner</label>
                  <select
                    className="form-select"
                    value={tossWinnerInput}
                    onChange={(event) => {
                      setTossWinnerInput(event.target.value);
                    }}
                    disabled={isTossLocked}
                  >
                    <option value="">Select toss winner</option>
                    {teams.map((team) => (
                      <option key={`toss-${team}`} value={team}>
                        {team}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label">Decision</label>
                  <select
                    className="form-select"
                    value={tossDecisionInput}
                    onChange={(event) => {
                      setTossDecisionInput(normalizeTossDecision(event.target.value));
                    }}
                    disabled={isTossLocked}
                  >
                    <option value="">Select decision</option>
                    <option value="bat">Bat</option>
                    <option value="field">Field</option>
                  </select>
                </div>
              </div>
              <div className="d-flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn btn-primary text-white fw-semibold"
                  onClick={handleStartMatch}
                  disabled={isStarting || isSavingToss || !canStart}
                >
                  {isStarting || isSavingToss ? 'Starting Match...' : 'Start Match'}
                </button>
              </div>
              {isTossLocked && (
                <p className="text-muted small mb-0">Toss locked after first ball.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {!showMobileTossOnly && showScoringSection && match && effectiveScoreStatus !== 'not_started' && (
        <div className="match-scoring-layout row g-4" aria-busy={mutationBlocked} data-testid="scoring-mutation-blocked" style={mutationBlocked ? { pointerEvents: 'none', opacity: 0.78 } : undefined}>
          <div className="match-scoring-summary-col col-12 col-lg-4 d-flex flex-column gap-3">
            <div className="match-scoring-summary-card card shadow-sm border-0">
              <div className="card-body d-flex flex-column gap-3">
                <div className="match-scoring-score-hero">
                  <p className="text-uppercase text-muted small mb-1">Score</p>
                  <h2 className="h2 mb-0">
                    {score.runs}-{score.wickets}
                  </h2>
                  <p className="text-muted mb-0">
                    Overs: {displayOvers}
                    {score.overs ? ` / ${score.overs}` : ''}
                  </p>
                  {hasPowerPlay ? (
                    <p className="text-muted mb-0">
                      Power plays: {formatPowerPlaySummary(powerPlays)}
                      {isPowerPlayOver ? (
                        <span className="badge bg-warning-subtle text-warning-emphasis ms-2">
                          Live
                        </span>
                      ) : null}
                    </p>
                  ) : null}
                </div>
                <div>
                  <p className="text-uppercase text-muted small mb-1">Innings</p>
                  <p className="mb-0 fw-semibold">Innings {currentInnings}</p>
                  {completedInnings.length > 0 && (
                    <div className="text-muted small">
                      {completedInnings.map((inning) => (
                        <div key={`inning-${inning.number}`}>
                          {`Innings ${inning.number}: ${inning.battingTeam} ${inning.runs || 0}-${inning.wickets || 0} (${oversFromBalls(inning.balls)}${inning.overs ? ` / ${inning.overs}` : ''})`}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-uppercase text-muted small mb-1">Batting</p>
                  <p className="mb-0 fw-semibold">{score.battingTeam}</p>
                  <p className="text-muted mb-0">Bowling: {score.bowlingTeam}</p>
                </div>
                <div>
                  <p className="text-uppercase text-muted small mb-1">Players</p>
                  <p className="mb-0 text-muted">
                    Striker: {resolvePlayerName(score.striker) || 'Not set'}
                  </p>
                  <p className="mb-0 text-muted">
                    Non-striker: {resolvePlayerName(score.nonStriker) || (isSoloLastBatterMode ? 'No partner required' : 'Not set')}
                  </p>
                  <p className="mb-0 text-muted">
                    Bowler: {resolvePlayerName(score.bowler) || 'Not set'}
                    {score.bowlerSide ? ` (${score.bowlerSide} side)` : ''}
                  </p>
                </div>
                <div>
                  <p className="text-uppercase text-muted small mb-1">Extras</p>
                  <div className="text-muted small">
                    Wides {scoreExtras.wides || 0} • No balls {scoreExtras.noBalls || 0} • Byes {scoreExtras.byes || 0}
                  </div>
                  <div className="text-muted small">
                    Leg byes {scoreExtras.legByes || 0} • Penalty {scoreExtras.penalty || 0}
                  </div>
                </div>
                <div>
                  <p className="text-uppercase text-muted small mb-1">Power plays</p>
                  {hasPowerPlay ? (
                    <div className="d-flex flex-column gap-1 mb-2">
                      {powerPlays.map((entry, index) => (
                        <div
                          className="d-flex align-items-center justify-content-between gap-2"
                          key={`power-play-${entry.startOver}-${entry.endOver}-${index}`}
                        >
                          <span className="text-muted small">{formatPowerPlayLabel(entry)}</span>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleRemovePowerPlay(index)}
                            disabled={isUpdating || isMatchComplete}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted small mb-2">No power plays set yet.</p>
                  )}
                  <div className="row g-2 align-items-end">
                    <div className="col-6">
                      <label className="form-label small mb-1">Start over</label>
                      <input
                        type="number"
                        min="1"
                        className="form-control form-control-sm"
                        value={powerPlayStartInput}
                        onChange={(event) => setPowerPlayStartInput(event.target.value)}
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label small mb-1">End over</label>
                      <input
                        type="number"
                        min="1"
                        className="form-control form-control-sm"
                        value={powerPlayEndInput}
                        onChange={(event) => setPowerPlayEndInput(event.target.value)}
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm mt-2"
                    onClick={handleAddPowerPlay}
                    disabled={isUpdating || isMatchComplete}
                  >
                    Add power play
                  </button>
                </div>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => handleScoreUpdate({ action: 'undo' })}
                  disabled={isUpdating || !canUndo}
                >
                  Undo last ball
                </button>
                {isAwaitingNextInnings && (
                  <div className="border rounded-3 p-3 bg-light-subtle">
                    <p className="mb-2 fw-semibold">First innings complete.</p>
                    <p className="text-muted small mb-3">
                      Start the next innings when you are ready. You can still undo the last delivery if needed.
                    </p>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => handleScoreUpdate({ action: 'start_next_innings' })}
                      disabled={isUpdating || isMatchComplete}
                    >
                      Start next innings
                    </button>
                  </div>
                )}
                {isTieScore && !score?.matchComplete ? (
                  <>
                    <div className="alert alert-warning mb-0" role="alert">
                      The match is tied. Start a super over or end the match as a tie.
                    </div>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={async () => {
                        if (!token || !tournamentId || !matchId) return;
                        if (!window.confirm('Start a super over now?')) return;
                        setIsStartingSuperOver(true);
                        setError('');
                        try {
                          await handleScoreUpdate({ action: 'start_super_over' });
                        } catch (err) {
                          setError(err.message || 'Unable to start a super over.');
                        } finally {
                          setIsStartingSuperOver(false);
                        }
                      }}
                      disabled={isStartingSuperOver || isEnding || isUpdating}
                    >
                      {isStartingSuperOver ? 'Starting super over...' : 'Start super over'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={async () => {
                        if (!token || !tournamentId || !matchId) return;
                        if (!window.confirm('End this match as a tie? This will lock the stats.')) return;
                        setIsEnding(true);
                        setError('');
                        try {
                          const response = await handleScoreUpdate({ action: 'end_match' });
                          if (response) navigate(schedulePath);
                        } catch (err) {
                          setError(err.message || 'Unable to end match.');
                        } finally {
                          setIsEnding(false);
                        }
                      }}
                      disabled={isEnding || isUpdating}
                    >
                      {isEnding ? 'Ending match...' : 'End match'}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    onClick={async () => {
                      if (!token || !tournamentId || !matchId) return;
                      if (!window.confirm('End this match? This will lock the stats.')) return;
                      setIsEnding(true);
                      setError('');
                      try {
                        const response = await handleScoreUpdate({ action: 'end_match' });
                        if (response) navigate(schedulePath);
                      } catch (err) {
                        setError(err.message || 'Unable to end match.');
                      } finally {
                        setIsEnding(false);
                      }
                    }}
                    disabled={isEnding || isUpdating || isMatchComplete}
                  >
                    {isEnding ? 'Ending match...' : 'End match'}
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="match-scoring-controls-col col-12 col-lg-8">
            <div className="match-scoring-controls-card card shadow-sm border-0">
              <div className="card-body d-flex flex-column gap-3">
                <h2 className="h5 mb-0 match-scoring-controls-title">Ball-by-ball controls</h2>
                <div className="match-scoring-panel match-scoring-player-panel border rounded-3 p-3 bg-light-subtle">
                  <p className="text-uppercase text-muted small mb-2">Players on field</p>
                  <div className="row g-3">
                    <div className="col-12 col-md-3">
                      <label className="form-label">Striker</label>
                      <select
                        className="form-select"
                        value={striker}
                        disabled={isAllOut || isCompleted || isMatchComplete}
                        onChange={handleStrikerChange}
                      >
                        <option value="">Select striker</option>
                        {groupedStrikerSelectorOptions.map((item, index) => (
                          item?.isLabel ? (
                            <option key={`striker-label-${index}`} value="" disabled>
                              {item.label}
                            </option>
                          ) : (
                            <option key={item.id} value={item.id}>
                              {item.name}
                            </option>
                          )
                        ))}
                      </select>                    </div>
                    <div className="col-12 col-md-3">
                      <label className="form-label">Non-striker{isSoloBatterActive ? ' (not required)' : ''}</label>
                      <select
                        className="form-select"
                        value={nonStriker}
                        disabled={isAllOut || isCompleted || isMatchComplete}
                        onChange={handleNonStrikerChange}
                      >
                        <option value="">{isSoloBatterActive ? 'No partner available' : 'Select non-striker'}</option>
                        {groupedNonStrikerSelectorOptions.map((item, index) => (
                          item?.isLabel ? (
                            <option key={`non-striker-label-${index}`} value="" disabled>
                              {item.label}
                            </option>
                          ) : (
                            <option key={item.id} value={item.id}>
                              {item.name}
                            </option>
                          )
                        ))}
                      </select>
                    </div>
                    <div className="col-12 col-md-3">
                      <label className="form-label match-scoring-bowler-label">
                        <span>Bowler</span>
                        {showBowlerJackpotBadge ? (
                          <span className="match-scoring-bowler-jackpot-badge" title="Jackpot over active" aria-label="Jackpot over active">
                            J
                          </span>
                        ) : null}
                      </label>
                      <select
                        className={`form-select${needsNewBowlerSelection ? ' is-invalid' : ''}`}
                        value={bowler}
                        disabled={isAllOut || isCompleted || isMatchComplete}
                        onChange={handleBowlerChange}
                      >
                        <option value="">Select bowler</option>
                        {groupedBowlerSelectorOptions.map((item, index) => (
                          item?.isLabel ? (
                            <option key={`bowler-label-${index}`} value="" disabled>
                              {item.label}
                            </option>
                          ) : (
                            <option key={item.id} value={item.id}>
                              {item.email ? `${item.name} (${item.email})` : item.name}
                            </option>
                          )
                        ))}
                      </select>
                      {fullQuotaRestrictionMessage ? <div className="form-text">{fullQuotaRestrictionMessage}</div> : null}
                      {bowlerSelectorOptions.length === 0 && (
                        <div className="form-text text-warning">Configure squad size and ensure team squads are available first</div>
                      )}
                    </div>
                    <div className="col-12 col-md-3">
                      <label className="form-label">Bowling side</label>
                      <select
                        className="form-select"
                        value={bowlerSide}
                        disabled={isAllOut || isCompleted || isMatchComplete}
                        onChange={handleBowlerSideChange}
                      >
                        <option value="">Select side</option>
                        <option value="over">Over side</option>
                        <option value="round">Round side</option>
                      </select>
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={handleSwapStrikers}
                      disabled={!striker || !nonStriker || isSoloBatterActive || isAllOut || isCompleted || isMatchComplete}
                    >
                      Switch strike
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={handlePlayersSave}
                      disabled={isUpdating || isAllOut || isCompleted || isMatchComplete}
                    >
                      {scorePrompts.needsNewBatter || scorePrompts.needsNewBowler ? 'Save player changes' : 'Save players'}
                    </button>
                    <span className="text-muted small align-self-center">
                      Strike will swap automatically on odd runs and at the end of each over.
                    </span>
                  </div>
                  {(needsNewBowler || bowlerQuotaBlocked) && (
                    <div className="alert alert-warning mt-3 mb-0" role="alert">
                      {bowlerQuotaBlocked
                        ? 'This bowler cannot continue. Please select another bowler.'
                        : 'Over completed. Please select next bowler.'}
                    </div>
                  )}
                  {((isAllOut && !isSoloLastBatterMode) ||
                    isCompleted ||
                    isMatchComplete ||
                    scorePrompts.needsNewBatter ||
                    !hasRequiredPlayers ||
                    shouldKeepInningsLiveForSoloBatter) && (
                    <div className="alert alert-warning mt-3 mb-0" role="alert">
                      {isAllOut && !isSoloLastBatterMode && (
                        <p className="mb-1">All out. The innings has ended.</p>
                      )}
                      {effectiveScoreStatus === 'completed' && (
                        <p className="mb-1">Overs limit reached. The innings has ended.</p>
                      )}
                      {scorePrompts.needsNewBatter && (
                        <p className="mb-1">
                          {scorePrompts.needsNewStriker && !scorePrompts.needsNewNonStriker
                            ? 'Wicket fell. Select a new striker before the next ball.'
                            : scorePrompts.needsNewNonStriker && !scorePrompts.needsNewStriker
                              ? 'Wicket fell. Select a new non-striker before the next ball.'
                              : 'Wicket fell. Select the new batter before the next ball.'}
                        </p>
                      )}
                      {shouldKeepInningsLiveForSoloBatter && (
                        <p className="mb-0">Solo last batter mode is active. Continue scoring with the remaining batter.</p>
                      )}
                      {!hasRequiredPlayers && (
                        <p className="mb-0">{isSoloBatterActive ? 'Select the striker and bowler before scoring.' : 'Select the striker, non-striker, and bowler before scoring.'}</p>
                      )}
                    </div>
                  )}
                </div>
                {isWicketConfirmOpen && pendingWicketPayload ? (
                  <>
                    <div className="modal-backdrop show" />
                    <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
                      <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-danger">
                          <div className="modal-header">
                            <div>
                              <h5 className="modal-title">Confirm wicket</h5>
                              <p className="text-muted mb-0 small">Please confirm this dismissal before recording it.</p>
                            </div>
                            <button
                              type="button"
                              className="btn-close"
                              aria-label="Close"
                              onClick={closeWicketConfirmModal}
                              disabled={isUpdating}
                            />
                          </div>
                          <div className="modal-body">
                            <div className="alert alert-warning" role="alert">
                              {pendingWicketPayload.wicketType === 'retired-hurt'
                                ? 'This will record a retired-hurt event and prompt the next batter selection.'
                                : isRunOutType(pendingWicketPayload.wicketType)
                                  ? 'This will record a run-out for the selected batter.'
                                  : 'This will record an out for the current batter.'}
                            </div>
                            <dl className="row mb-0">
                              {pendingWicketSummary.map((item) => (
                                <div key={item.label} className="col-12 mb-2">
                                  <dt className="text-uppercase text-muted small mb-1">{item.label}</dt>
                                  <dd className="mb-0 fw-semibold">{item.value}</dd>
                                </div>
                              ))}
                            </dl>
                          </div>
                          <div className="modal-footer">
                            <button type="button" className="btn btn-outline-secondary" onClick={closeWicketConfirmModal} disabled={isUpdating}>
                              Cancel
                            </button>
                            <button type="button" className="btn btn-danger" onClick={confirmPendingWicket} disabled={isUpdating}>
                              {isUpdating ? 'Recording…' : 'Confirm wicket'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : null}

                <div className="match-scoring-panel match-scoring-shot-panel border rounded-3 p-3">
                  <p className="text-uppercase text-muted small mb-2">Shot details</p>
                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <label className="form-label">Shot type/style</label>
                      <select
                        className="form-select"
                        value={shotType}
                        onChange={(event) => setShotType(event.target.value)}
                      >
                        <option value="">Select shot style</option>
                        {shotTypeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <small className="text-muted d-block mt-1">
                        All styles: {shotTypeOptions.map((option) => option.label).join(', ')}.
                      </small>
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label">Shot placement</label>
                      <select
                        className="form-select"
                        value={shotPlacement}
                        onChange={(event) => setShotPlacement(event.target.value)}
                      >
                        <option value="">Select placement</option>
                        {shotPlacementOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <small className="text-muted d-block mt-1">
                        All placements: {shotPlacementOptions.map((option) => option.label).join(', ')}.
                      </small>
                    </div>
                  </div>
                </div>
                <div className="match-scoring-run-panel d-flex flex-wrap align-items-center gap-3">
                  <label className="form-check-label match-scoring-jackpot-toggle">
                    <input
                      type="checkbox"
                      className="form-check-input me-2"
                      checked={isJackpotOver}
                      onChange={handleJackpotOverToggle}
                      disabled={isUpdating || scoringLocked || !canToggleJackpotOver}
                    />
                    Jackpot Over
                  </label>
                  <small className="text-muted">
                    {canToggleJackpotOver
                      ? 'Choose jackpot before ball 1 of this over.'
                      : 'Jackpot locks after the first ball of the over.'}
                  </small>
                  {[0, 1, 2, 3, 4, 5, 6].map((run) => (
                    <button
                      key={run}
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={() => handleScoreUpdate(withBallMetadata({ action: 'ball', runs: run }))}
                      disabled={isUpdating || scoringLocked}
                    >
                      {run}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={() => {
                      openWicketConfirmModal(withBallMetadata({ action: 'ball', runs: 0, isWicket: true }));
                    }}
                    disabled={isUpdating || scoringLocked}
                  >
                    Wicket
                  </button>
                </div>
                <div className="match-scoring-panel match-scoring-wicket-panel border rounded-3 p-3">
                  <p className="text-uppercase text-muted small mb-2">Wicket details</p>
                  <div className="row g-3">
                    <div className="col-12 col-md-4">
                      <label className="form-label">How out</label>
                      <select
                        className="form-select"
                        value={wicketType}
                        onChange={(event) => setWicketType(event.target.value)}
                      >
                        <option value="">Select dismissal</option>
                        {wicketTypeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    {isRunOutType(wicketType) ? (
                      <>
                        <div className="col-12 col-md-4">
                          <label className="form-label">Who got run out</label>
                          <select
                            className="form-select"
                            value={runOutWho}
                            onChange={(event) => setRunOutWho(event.target.value)}
                          >
                            <option value="">Select batter</option>
                            <option value="striker">Striker</option>
                            <option value="non-striker">Non-striker</option>
                          </select>
                        </div>
                        <div className="col-12 col-md-4">
                          <label className="form-label">Run-out timing</label>
                          <select
                            className="form-select"
                            value={runOutCompletedRuns}
                            onChange={(event) => setRunOutCompletedRuns(event.target.value)}
                          >
                            <option value="">Select run number</option>
                            {runOutRunOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-12 col-md-4">
                          <label className="form-label">Thrower</label>
                          <select
                            className="form-select"
                            value={runOutThrower}
                            onChange={(event) => setRunOutThrower(event.target.value)}
                          >
                            <option value="">Select thrower</option>
                            {bowlingAnnouncedPlayers.map((player) => (
                              <option key={player.id} value={player.id}>
                                {player.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-12 col-md-4">
                          <label className="form-label">Who hit wicket</label>
                          <select
                            className="form-select"
                            value={runOutWicketHitter}
                            onChange={(event) => setRunOutWicketHitter(event.target.value)}
                          >
                            <option value="">Select wicket hitter</option>
                            {bowlingAnnouncedPlayers.map((player) => (
                              <option key={player.id} value={player.id}>
                                {player.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="col-12 col-md-4">
                          <label className="form-label">Where out</label>
                          <select
                            className="form-select"
                            value={wicketLocation}
                            onChange={(event) => setWicketLocation(event.target.value)}
                          >
                            <option value="">Select location</option>
                            {wicketLocationOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-12 col-md-4">
                          <label className="form-label">Fielder</label>
                          <select
                            className="form-select"
                            value={wicketFielder}
                            onChange={(event) => setWicketFielder(event.target.value)}
                          >
                            <option value="">Select fielder</option>
                            {bowlingAnnouncedPlayers.map((player) => (
                              <option key={player.id} value={player.id}>
                                {player.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-2">
                    <small className="text-muted">
                      Required for caught (excluding caught and bowled), run out, and stumped dismissals.
                    </small>
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={resetWicketDetails}>
                      Clear wicket details
                    </button>
                  </div>
                  {wicketError && (
                    <div className="alert alert-warning mt-3 mb-0" role="alert">
                      {wicketError}
                    </div>
                  )}
                </div>
                <div className="match-scoring-panel match-scoring-extras-panel">
                  <p className="text-uppercase text-muted small mb-2">Extras</p>
                  <div className="d-flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => handleScoreUpdate(withBallMetadata({ action: 'ball', runs: 0, extraType: 'wide' }))}
                      disabled={isUpdating || scoringLocked}
                    >
                      Wide
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => handleScoreUpdate(withBallMetadata({ action: 'ball', runs: 0, extraType: 'no-ball' }))}
                      disabled={isUpdating || scoringLocked}
                    >
                      No ball
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => handleScoreUpdate(withBallMetadata({ action: 'ball', runs: 1, extraType: 'bye' }))}
                      disabled={isUpdating || scoringLocked}
                    >
                      Bye
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => handleScoreUpdate(withBallMetadata({ action: 'ball', runs: 1, extraType: 'leg-bye' }))}
                      disabled={isUpdating || scoringLocked}
                    >
                      Leg bye
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => handleScoreUpdate(withBallMetadata({ action: 'ball', runs: 5, extraType: 'penalty', countBall: false }))}
                      disabled={isUpdating || scoringLocked}
                    >
                      Penalty (5)
                    </button>
                  </div>
                </div>
                <div className="match-scoring-panel match-scoring-advanced-panel border rounded-3 p-3 bg-light-subtle">
                  <p className="text-uppercase text-muted small mb-2">Advanced options</p>
                  <div className="row g-3 align-items-end">
                    <div className="col-12 col-md-3">
                      <label className="form-label">Runs (excluding automatic extra)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={advancedRuns}
                        onChange={(event) => setAdvancedRuns(event.target.value)}
                      />
                      <small className="text-muted">Wide/no-ball adds +1 automatically.</small>
                    </div>
                    <div className="col-12 col-md-3">
                      <label className="form-label">Extra type</label>
                      <select
                        className="form-select"
                        value={advancedExtraType}
                        onChange={handleAdvancedExtraChange}
                      >
                        <option value="">None</option>
                        <option value="wide">Wide</option>
                        <option value="no-ball">No ball</option>
                        <option value="bye">Bye</option>
                        <option value="leg-bye">Leg bye</option>
                        <option value="penalty">Penalty</option>
                        <option value="jackpot-plus">Jackpot plus</option>
                        <option value="jackpot-minus">Jackpot minus</option>
                      </select>
                    </div>
                    <div className="col-12 col-md-3">
                      <label className="form-label d-block">Count ball</label>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="count-ball-toggle"
                          checked={advancedCountBall}
                          onChange={(event) => setAdvancedCountBall(event.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="count-ball-toggle">
                          Ball counts in over
                        </label>
                      </div>
                    </div>
                    <div className="col-12 col-md-3">
                      <label className="form-label d-block">Wicket</label>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="wicket-toggle"
                          checked={advancedIsWicket}
                          onChange={(event) => setAdvancedIsWicket(event.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="wicket-toggle">
                          Wicket fell
                        </label>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-primary mt-3"
                    onClick={handleAdvancedSubmit}
                    disabled={isUpdating || scoringLocked}
                  >
                    Add ball
                  </button>
                </div>
                <div className="match-scoring-panel match-scoring-current-over-panel border rounded-3 p-3">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                    <p className="text-uppercase text-muted small mb-0">Current over</p>
                    <span className="text-muted small">
                      {displayOver.ballsInOver}/6 balls
                    </span>
                  </div>
                  {currentOverRows.length === 0 && !isJackpotOver ? (
                    <div className="alert alert-light mb-0">
                      Over complete. Next over deliveries will appear here.
                    </div>
                  ) : (
                    <div className="over-balls">
                      {currentOverRows.length === 0 && isJackpotOver ? (
                        <span
                          className="over-ball over-ball--extra"
                          title="Jackpot over selected"
                          aria-label="Jackpot over selected"
                        >
                          J
                        </span>
                      ) : null}
                      {currentOverRows.map(({ label, event }, index) => (
                        <span
                          key={`${event.timestamp || 'over-ball'}-${index}`}
                          className={`over-ball${isActualWicket(event) ? ' over-ball--wicket' : ''}${
                            event.extraType ? ' over-ball--extra' : ''
                          }`}
                          title={`${label} ball${getRecentBallTitle(event) ? ` • ${getRecentBallTitle(event)}` : ''}`}
                        >
                          {formatBallDisplay(event)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {events.length > 0 && (
                  <div className="match-scoring-panel match-scoring-recent-panel">
                    <p className="text-uppercase text-muted small mb-2">Recent balls</p>
                    <div className="d-flex flex-wrap gap-2">
                      {events.slice(-12).map((event, index) => (
                        <span
                          key={`${event.timestamp || 'ball'}-${index}`}
                          className="badge bg-light text-dark"
                          title={getRecentBallTitle(event)}
                        >
                          {formatBallDisplay(event)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  
    </PageShell>
  );
};

export default MatchStartPage;
